<template>
  <f7-page :page-content="false">
    <!-- La ayuda de esta pantalla vive en la colección `ayudas` y la abre el icono global del appbar. -->
    <f7-navbar title="Clasificación de items" :back-link="$t('atras')"></f7-navbar>
    <f7-page-content class="full-height">
      <!-- La tanda dura minutos y corre por detrás: sin esto, lo único que se ve es el botón
           deshabilitado poniendo «Calculando…», que no dice ni por cuál va ni cuánto queda. -->
      <div v-if="sugerenciaEnCurso" class="creacion-banner">
        <i class="f7-icons ia-text" style="font-size:16px;">wand_stars</i>
        <span style="font-weight:600;">Calculando propuestas de características</span>
        <span v-if="sugerenciaAvance && sugerenciaAvance.texto" class="text-color-gray">{{ sugerenciaAvance.texto }}</span>
        <span v-if="sugerenciaAvance && sugerenciaAvance.total" class="text-color-gray" style="font-size:11px;">
          {{ sugerenciaAvance.hechos }} / {{ sugerenciaAvance.total }}
        </span>
        <span class="text-color-gray" style="font-size:11px;">Puedes seguir trabajando; al abrir cada clasificación estará lista.</span>
        <f7-button small round color="red" style="margin-left:auto;" :disabled="sugerenciaCancelando"
                   @click="cancelarSugerenciaMasiva">
          {{ sugerenciaCancelando ? 'Cancelando…' : 'Cancelar' }}
        </f7-button>
      </div>
      <r-dynamic-list
        ref="rlist"
        :dbAdapter="ItemClasificacion"
        :tableCode="'ITEMS_CLASIFICACION'"
        :tableMode="true"
        :virtualListParams="{height:32, rowsBefore:60, rowsAfter:60}"
        class="card full-height data-table"
        classContent="card-content full-height"
        :massiveActions="true"
        :mandatoryFilter="{ estadoTipificacionDistinto: 'FUSIONADA' }"
        :controlsWidth="100"
      >
        <template v-slot:tableLinks>
          <div class="tablelinks-menu">
            <a class="link tablelink-row" @click="onImportClick">
              <i class="f7-icons">square_arrow_down</i>
              <span>Importar Excel</span>
            </a>
            <a class="link tablelink-row ia-text" @click="abrirRevision" style="margin-left: 0;">
              <i class="f7-icons">wand_stars</i>
              <span>Revisar formatos</span>
            </a>
            <a class="link tablelink-row ia-text" @click="abrirDiscriminantes" style="margin-left: 0;"
               v-tooltip="'Revisar con IA qué valores distinguen unos subtipos de otros'">
              <i class="f7-icons">slider_horizontal_3</i>
              <span>Discriminantes</span>
            </a>
            <a class="link tablelink-row" href="/itemclasificacion/new/"  style="margin-left: 0;">
              <i class="f7-icons">plus</i>
              <span>{{$t('common.nueva')}}</span>
            </a>
          </div>
          <input ref="fileInput" type="file" accept=".xlsx,.xls" style="display:none" @change="onFileSelected" />
        </template>
        <template v-slot:extendedActions>
          <f7-link small icon-f7="sparkles" color="teal" @click="abrirGenerar"
            v-tooltip="'Generar items enriquecidos desde expedientes'"></f7-link>
        </template>
        <template v-slot:massiveActions="{ selecteds }">
          <f7-button v-if="Object.keys(selecteds).length >= 2" class="display-flex" fill color="purple"
            icon-f7="arrow_merge" @click="abrirFusion(selecteds)">Fusionar</f7-button>
          <!-- Agrupar es una llamada a la IA de varios minutos por clasificación. Lanzarlas de una
               en una desde cada ficha obliga a esperar delante de la pantalla tantas veces como
               clasificaciones tenga la materia. Aquí se calculan todas por detrás y luego cada
               ficha las abre al instante. -->
          <f7-button v-if="Object.keys(selecteds).length" class="display-flex" fill color="blue"
            icon-f7="wand_stars" :disabled="sugerenciaEnCurso"
            @click="lanzarSugerenciaMasiva(selecteds)">
            {{ sugerenciaEnCurso ? 'Calculando…' : 'Sugerir características (' + Object.keys(selecteds).length + ')' }}
          </f7-button>
        </template>
        <template v-slot:column-codigo="{item}">
          <a
            v-if="item.codigo"
            :href="urlVerTipo(item.codigo)"
            target="_blank"
            rel="noopener"
            class="external"
            v-tooltip="'Ver tipo ' + item.codigo + ' en nueva pestaña'"
            style="text-decoration:none;cursor:pointer;"
          >
            <span
              :class="'badge color-' + (coloresPorCodigo[item.codigo] || 'gray')"
              style="padding:3px 12px;font-weight:600;letter-spacing:0.3px;"
            >{{ item.codigo }}</span>
          </a>
        </template>
        <template v-slot:column-subtipos="{item}">
          <span
            v-if="item.subtipos && item.subtipos.length"
            style="display:inline-flex;flex-wrap:nowrap;gap:3px;overflow:hidden;max-width:100%;align-items:center;"
            :title="item.subtipos.join(' / ')"
          >
            <span
              v-for="(s, si) in item.subtipos"
              :key="s"
              class="badge color-gray"
              :style="estiloSubtipo(si)"
            >{{ s }}</span>
          </span>
          <span v-else class="text-color-gray">—</span>
        </template>
        <template v-slot:column-materias="{item}">
          <span v-if="item.materias && item.materias.length" style="display:inline-flex;flex-wrap:wrap;gap:3px;">
            <span
              v-for="m in item.materias"
              :key="(m && (m.codigo || m.id)) || Math.random()"
              class="badge color-blue"
              style="padding:2px 8px;font-size:11px;font-weight:500;"
              :title="m && m.nombre ? m.nombre : ''"
            >{{ (m && (m.codigo || m.nombre)) || '?' }}</span>
          </span>
          <span v-else class="text-color-gray">—</span>
        </template>
        <template v-slot:optionsColumn="{item}">
          <f7-link icon-f7="eye" color="green" :href="`/itemclasificacion/view/${encodeURIComponent(item.id)}/`" v-tooltip="$t('common.ver')"></f7-link>
          <f7-link icon-f7="square_pencil" color="blue" :href="`/itemclasificacion/edit/${encodeURIComponent(item.id)}/`" v-tooltip="$t('common.editar')"></f7-link>
          <f7-link icon-f7="trash" color="red" @click="$refs.rlist.deleteItem(item.id)" v-tooltip="$t('common.eliminar')"></f7-link>
        </template>
      </r-dynamic-list>
    </f7-page-content>

    <!-- Popup: condiciones de discriminación entre subtipos (IA) -->
    <f7-popup class="popup-revision-formatos" :opened="discPopupOpened" @popup:closed="cerrarDiscriminantes">
      <f7-page>
        <f7-navbar>
          <f7-nav-title><span class="titulo-revision ia-text">Discriminantes de subtipo</span></f7-nav-title>
          <f7-nav-right><f7-link popup-close>Cerrar</f7-link></f7-nav-right>
        </f7-navbar>

        <div class="revision-toolbar">
          <select v-model="discCodigo" class="fusion-select" :disabled="discCargando" style="min-width:180px;"
                  @change="cargarEmpates">
            <option value="">Elige un tipo…</option>
            <option v-for="t in tiposMaestro" :key="'dt-'+t.codigo" :value="t.codigo">
              {{ t.codigo }}{{ t.denominacion ? ' - ' + t.denominacion : '' }}
            </option>
          </select>
          <f7-button small fill class="ia-btn" @click="revisarDiscriminantes" :disabled="discCargando || !discCodigo">
            <i class="f7-icons" style="font-size:15px;">wand_stars</i>
            Revisar con IA
          </f7-button>
          <f7-button v-if="discCargando" small round color="red" @click="cancelarDiscriminantes">Cancelar</f7-button>
          <span v-if="discResultado && discResultado.modelo" class="text-color-gray" style="font-size:11px;">
            modelo: {{ discResultado.modelo }}
          </span>
          <f7-button v-if="discPropuestas.length" small fill color="green" style="margin-left:auto;"
                     @click="guardarDiscriminantes" :disabled="discGuardando">
            {{ discGuardando ? 'Guardando…' : 'Guardar condiciones' }}
          </f7-button>
        </div>

        <div class="padding-horizontal text-color-gray" style="font-size:11px;">
          La clasificación de cada item es determinista y no usa IA. Estas condiciones se guardan en el maestro
          y se evalúan luego sin coste. Una condición equivocada descarta el subtipo: revísalas antes de guardar.
        </div>

        <!-- Empates registrados: varias clasificaciones cumplieron a la vez. Suele delatar
             una condición accesoria, así que se muestran aunque no se haya lanzado la revisión. -->
        <div v-if="discEmpates.length" class="padding-horizontal" style="margin-top:8px;">
          <div class="text-color-gray" style="font-size:11px;font-weight:600;">
            Empates detectados al clasificar ({{ discEmpates.length }}):
          </div>
          <div v-for="(e, ei) in discEmpates" :key="'emp-'+ei" class="disc-empate">
            <div>
              <span class="badge color-red" style="font-size:10px;">{{ e.veces }}×</span>
              <span v-for="(d, di) in (e.descripciones || [])" :key="'ed-'+ei+'-'+di"
                    class="badge color-purple" style="font-size:10px;margin-left:3px;">{{ d }}</span>
              <span class="text-color-gray" style="font-size:10px;margin-left:6px;">
                resuelto por {{ e.resueltoPor }}{{ e.clasificacionElegida ? ' → ' + e.clasificacionElegida : '' }}
              </span>
            </div>
            <div v-if="e.justificacion" class="disc-just">{{ e.justificacion }}</div>
            <div v-for="(a, ai) in (e.condicionesAccesorias || [])" :key="'ea-'+ei+'-'+ai"
                 style="font-size:10px;color:#b36b00;">condición poco discriminante: {{ a }}</div>
          </div>
        </div>

        <r-ia-pensando v-if="discCargando" :narracion="discNarracion" latin-default="Thinking"
                       texto-default="Analizando las opciones declaradas en los formatos…" />

        <div v-else-if="discResultado">
          <!-- Avisos de calidad de datos -->
          <div v-if="(discResultado.avisos || []).length" class="padding-horizontal" style="margin-top:8px;">
            <div v-for="(a, ai) in discResultado.avisos" :key="'av-'+ai" class="disc-aviso">
              <i class="f7-icons" style="font-size:14px;">exclamationmark_triangle</i>
              <span><strong>{{ a.caracteristica }}</strong>{{ a.formatoCodigo ? ' (' + a.formatoCodigo + ')' : '' }}: {{ a.mensaje }}</span>
            </div>
          </div>

          <!-- Opciones que no encajaron en ningún subtipo -->
          <div v-if="(discResultado.opcionesHuerfanas || []).length" class="padding-horizontal" style="margin-top:8px;">
            <div class="text-color-gray" style="font-size:11px;font-weight:600;">Opciones sin subtipo asignado:</div>
            <span v-for="(o, oi) in discResultado.opcionesHuerfanas" :key="'oh-'+oi" class="badge color-orange"
                  style="font-size:10px;margin:2px;" :title="o.motivo">{{ o.caracteristica }} = {{ o.opcion }}</span>
          </div>

          <div v-if="discResultado.observaciones" class="padding-horizontal text-color-gray"
               style="font-size:11px;margin-top:6px;font-style:italic;">{{ discResultado.observaciones }}</div>

          <!-- Leyenda: cómo se lee una condición -->
          <div v-if="discPropuestas.length" class="disc-leyenda">
            <b>Cómo leer esto:</b> cada tarjeta es un subtipo (chips morados). Debajo van sus <b>condiciones</b>:
            un item se clasifica en ese subtipo si el <b>campo</b> del expediente cumple lo indicado.
            «contiene» = el valor del expediente incluye alguno de los valores listados. Los valores salen de las
            <b>opciones declaradas</b> en los formatos; edítalos con los chips (la <i class="f7-icons" style="font-size:11px;">xmark_circle_fill</i> los quita).
            Un subtipo <b>sin condiciones</b> no se distingue por valor y no se aplica ninguna regla.
          </div>

          <!-- Propuesta por subtipo -->
          <div v-for="(p, pi) in discPropuestas" :key="'dp-'+pi" class="disc-card">
            <div class="disc-card-head">
              <span v-for="(s, si) in (p.subtipos || [])" :key="'ds-'+pi+'-'+si" class="badge color-purple"
                    style="font-size:10px;">{{ s }}</span>
              <span v-if="p.confianza != null" class="text-color-gray" style="font-size:11px;margin-left:auto;">
                confianza {{ Math.round(p.confianza * 100) }}%
              </span>
            </div>

            <div v-if="!p.condiciones.length" class="text-color-gray" style="font-size:11px;padding:4px 0;">
              Sin condiciones: este subtipo no discrimina por valor.
            </div>

            <div v-for="(c, ci) in p.condiciones" :key="'dc-'+pi+'-'+ci" class="disc-cond">
              <!-- Línea 1: la condición leída como frase -->
              <div class="disc-cond-linea">
                <span class="disc-si">Si el campo</span>
                <div class="disc-carac-wrap">
                  <select v-model="c.caracteristica" class="fusion-select disc-carac">
                    <option v-for="ca in (discResultado.caracteristicas || [])" :key="'cc-'+pi+'-'+ci+'-'+ca.nombre" :value="ca.nombre">
                      {{ ca.label || ca.nombre }}
                    </option>
                  </select>
                  <!-- nombre técnico real del campo, para distinguir variantes (tipoinstalacion vs tipo_de_instalación) -->
                  <code class="disc-nombre-tec">{{ c.caracteristica }}<span class="disc-opc-count">· {{ opcionesDeclaradas(c.caracteristica).length }} opc.</span></code>
                </div>
                <select v-model="c.operador" class="fusion-select disc-op" v-tooltip="ayudaOperador(c.operador)">
                  <option v-for="op in OPERADORES" :key="'op-'+pi+'-'+ci+'-'+op" :value="op">{{ etiquetaOperador(op) }}</option>
                </select>

                <!-- Numéricos: el valor va en la misma línea -->
                <template v-if="esOperadorNumerico(c.operador)">
                  <input class="rp-input" type="number" v-model.number="c.valorNumerico" placeholder="valor" style="width:90px;" />
                  <span v-if="c.operador === 'ENTRE'" class="disc-si">y</span>
                  <input v-if="c.operador === 'ENTRE'" class="rp-input" type="number" v-model.number="c.valorNumericoHasta"
                         placeholder="valor" style="width:90px;" />
                  <span v-if="c.unidad" class="text-color-gray" style="font-size:11px;">{{ c.unidad }}</span>
                </template>
                <span v-else-if="c.operador === 'EXISTE'" class="text-color-gray" style="font-size:11px;">
                  (basta con que el campo esté relleno, sea cual sea su valor)
                </span>

                <f7-link icon-f7="trash" color="red" @click="p.condiciones.splice(ci, 1)"
                         v-tooltip="'Quitar condición'" style="margin-left:auto;"></f7-link>
              </div>

              <!-- Línea 2 (solo texto): los VALORES que cumplen, siempre visibles y editables -->
              <div v-if="!esOperadorNumerico(c.operador) && c.operador !== 'EXISTE'" class="disc-valores">
                <span class="disc-valores-lbl">contiene alguno de:</span>
                <span v-for="(v, vi) in (c.valores || [])" :key="'dv-'+pi+'-'+ci+'-'+vi"
                      class="chip-valor" :title="v" @click="quitarValor(c, vi)">
                  {{ v }} <i class="f7-icons chip-x">xmark_circle_fill</i>
                </span>
                <span v-if="!(c.valores || []).length" class="disc-falta">⚠ falta el valor: elige uno abajo</span>

                <!-- Añadir: opciones declaradas del campo, o texto libre si no hay -->
                <select v-if="opcionesParaAnadir(c).length" class="fusion-select disc-add"
                        @change="anadirValorSelect(c, $event)">
                  <option value="">+ añadir valor declarado…</option>
                  <option v-for="(op, oi) in opcionesParaAnadir(c)" :key="'da-'+pi+'-'+ci+'-'+oi" :value="op">{{ op }}</option>
                </select>
                <input v-else class="rp-input disc-add-libre" placeholder="+ escribe un valor y Enter"
                       @keyup.enter="anadirValorTexto(c, $event)" />
              </div>

              <div v-if="c.justificacion" class="disc-just">{{ c.justificacion }}</div>
            </div>

            <f7-button small @click="anadirCondicion(p)" style="align-self:flex-start;">+ Añadir condición</f7-button>
          </div>
        </div>
      </f7-page>
    </f7-popup>

    <!-- Popup: revisión de formatos con IA -->
    <f7-popup class="popup-revision-formatos" :opened="revisionPopupOpened" @popup:closed="revisionPopupOpened = false">
      <f7-page>
        <f7-navbar>
          <f7-nav-title><span class="titulo-revision ia-text">Revisar formatos</span></f7-nav-title>
          <f7-nav-right>
            <f7-link popup-close>Cerrar</f7-link>
          </f7-nav-right>
        </f7-navbar>

        <div class="revision-modos">
          <f7-button small round :fill="vistaDeteccion" @click="vistaDeteccion = true">Detección (IA)</f7-button>
          <f7-button small round :fill="!vistaDeteccion" @click="vistaDeteccion = false">Formatos</f7-button>
        </div>

        <!-- ===================== Vista DETECCIÓN (clasificaciones propuestas) ===================== -->
        <template v-if="vistaDeteccion">
          <div class="revision-toolbar">
            <f7-button small fill class="ia-btn" @click="detectar" :disabled="deteccionCargando">
              <i class="f7-icons" style="font-size:15px;">wand_stars</i>
              Detectar clasificaciones
            </f7-button>
            <span v-if="deteccionInfo" class="text-color-gray" style="font-size:12px;">
              {{ clasifNuevasVisibles.length }}/{{ clasifNuevas.length }} nuevas · {{ formatosEnExistentes }} a clasificaciones existentes · {{ deteccionInfo.totalFormatos }} formatos
            </span>
            <input v-if="deteccionInfo && clasifNuevas.length" class="revision-filtro" type="search" placeholder="Filtrar sugerencias…" v-model="deteccionFiltro" />
            <label v-if="deteccionInfo && clasifNuevasVisibles.length" class="revision-check" style="display:flex; align-items:center; gap:5px; font-size:12px;">
              <input type="checkbox" :checked="todasVisiblesMarcadas" @change="marcarTodasVisibles($event.target.checked)" /> Todo lo visible
            </label>
            <f7-button small fill color="green" @click="crearNuevasSeleccionadas" :disabled="creando || numNuevasSel === 0">
              Crear seleccionadas ({{ numNuevasSel }})
            </f7-button>
            <f7-button v-if="filasVincular.length" small fill color="blue" @click="vincularSeleccionados"
                       :disabled="vinculando || numExistentesSel === 0">
              {{ vinculando ? 'Vinculando…' : 'Vincular seleccionados (' + numExistentesSel + ')' }}
            </f7-button>
          </div>
          <div v-if="!deteccionCargando && materiasPendientesLista.length" class="revision-materias-filtro">
            <span class="text-color-gray" style="font-size:11px;">Materias a detectar (vacío = todas):</span>
            <span v-for="m in materiasPendientesLista" :key="'mat-'+m.codigo"
                  class="materia-chip" :class="{ 'materia-chip-on': materiasFiltro.indexOf(m.codigo) >= 0 }"
                  :title="m.nombre" @click="toggleMateriaFiltro(m.codigo)">{{ m.codigo }} ({{ m.n }})</span>
            <a v-if="materiasFiltro.length" class="link" style="font-size:11px;" @click="materiasFiltro = []">limpiar</a>
          </div>
          <div v-if="creando && narracionCreacion" class="creacion-banner">
            <i class="f7-icons" style="font-size:16px;">hammer</i>
            <span class="ia-text" style="font-weight:600;">{{ narracionCreacion.latin }}</span>
            <span class="text-color-gray">{{ narracionCreacion.texto }}</span>
            <span v-if="narracionCreacion.total" class="text-color-gray" style="font-size:11px;">{{ narracionCreacion.hechos }} / {{ narracionCreacion.total }}</span>
            <f7-button small round color="red" style="margin-left:auto;" :disabled="creacionCancelando" @click="cancelarCreacion">
              {{ creacionCancelando ? 'Cancelando…' : 'Cancelar' }}
            </f7-button>
          </div>

          <r-ia-pensando v-if="deteccionCargando" :narracion="narracion" latin-default="Thinking" texto-default="Analizando formatos…" />

          <!-- Formatos que encajan en una clasificación que YA existe: no hay que crear nada,
               basta con declararlos en ella para que la clasificación de items los tenga en
               cuenta. Antes esto solo se contaba ("N ya cubiertas") y no se podía aplicar. -->
          <div v-if="!deteccionCargando && filasVincularVisibles.length" class="revision-lista">
            <div class="revision-subtitulo">
              Encajan en clasificaciones que ya existen
              <span class="text-color-gray">· se añade el formato a la clasificación marcada; no se crea ninguna nueva</span>
            </div>
            <div v-for="f in filasVincularVisibles" :key="'ce-'+f.codigo" class="revision-card revision-card-existente">
              <div class="revision-head">
                <label class="revision-check"><input type="checkbox" v-model="f._sel" /></label>
                <code class="revision-codigo">{{ f.codigo }}</code>
                <span class="text-color-gray" style="font-size:11px;">{{ f.nombre }}</span>
                <span class="rp-impacto">{{ f.expedientes || 0 }} exp · {{ f.expedientesConItem || 0 }} con item</span>
                <span v-if="!f.destinos.length" class="badge color-red" style="font-size:10px;">sin destino</span>
                <a v-if="f._origen" class="link rp-mover" style="margin-left:auto;"
                   @click="devolverANueva(f)">↩ volver a nueva</a>
                <a v-else class="link rp-mover" style="margin-left:auto;"
                   title="Llevarlo a Formatos para que la IA le proponga una clasificación propia, editable"
                   @click="proponerComoNueva(f)">quiero una nueva →</a>
              </div>
              <div v-if="materiasQueSeAnaden(f).length" class="destino-aviso">
                Se añadirá la materia <b>{{ materiasQueSeAnaden(f).join(', ') }}</b> a la clasificación:
                sin ella, los expedientes cuyo servicio sea de esa materia no llegan siquiera a considerarla.
              </div>
              <!-- El destino se puede corregir: la IA propone uno, pero un mismo formato puede
                   pertenecer a varias clasificaciones (p. ej. líneas aéreas y subterráneas) y ahí
                   ya decide el discriminante. -->
              <div class="rp-grupo-detalle">
                <span class="destino-label">Clasificación destino:</span>
                <span v-for="(cl, ci) in destinosDe(f)" :key="'dest-'+f.codigo+'-'+ci"
                      class="destino-chip" :class="{ 'destino-chip-on': f.destinos.indexOf(cl.idClasificacion) >= 0 }"
                      :title="cl.denominacion || ''" @click="toggleDestino(f, cl.idClasificacion)">
                  {{ cl.tipo }}<span v-if="(cl.subtipos || []).length"> · {{ (cl.subtipos || []).join(' + ') }}</span>
                  <i v-if="cl.idClasificacion === f.propuesta" class="f7-icons destino-ia" title="Propuesta por la IA">wand_stars</i>
                </span>
                <span v-if="!destinosDe(f).length" class="text-color-gray" style="font-size:11px;">
                  No hay clasificaciones para la materia {{ f.materiaCodigo || '?' }}.
                </span>
              </div>
            </div>
          </div>

          <div v-if="!deteccionCargando && !clasifNuevas.length && filasVincular.length" class="text-color-gray padding">
            No hay clasificaciones nuevas que crear; los formatos pendientes encajan en las que ya existen.
          </div>
          <div v-else-if="!deteccionCargando && !clasifNuevas.length && deteccionInfo" class="text-color-gray padding">
            No se han detectado clasificaciones nuevas (todos los formatos ya están cubiertos).
          </div>
          <div v-else-if="!deteccionCargando && !deteccionInfo" class="text-color-gray padding">
            Pulsa «Detectar clasificaciones» para que la IA agrupe los formatos activos y proponga las clasificaciones que faltan.
          </div>
          <div v-else-if="!deteccionCargando && !clasifNuevasVisibles.length" class="text-color-gray padding">Ninguna sugerencia coincide con el filtro.</div>
          <div v-else-if="!deteccionCargando" class="revision-lista">
            <div v-for="c in clasifNuevasVisibles" :key="'cn-'+c._idx" class="revision-card revision-card-nueva">
              <div class="revision-head">
                <label class="revision-check"><input type="checkbox" v-model="c._sel" /></label>
                <code class="revision-codigo">{{ c.tipo }}</code>
                <span v-for="(s, si) in (c.subtipos || [])" :key="'sn-'+c._idx+'-'+si" class="badge color-purple" style="font-size:10px;">{{ s }}</span>
                <span class="badge" :class="c.naturaleza === 'MOV' ? 'color-orange' : 'color-teal'" style="font-size:10px;">{{ c.naturaleza || '—' }}</span>
                <span class="text-color-gray" style="font-size:11px;">{{ (c.formatos || []).length }} formato(s)</span>
                <span class="text-color-gray" style="font-size:11px;" v-if="(c.materiasCodigos || []).length">materias: {{ (c.materiasCodigos || []).join(', ') }}</span>
                <f7-link class="revision-btn-proponer" @click="toggleNueva(c._idx)">{{ nuevaAbierta === c._idx ? 'ocultar' : 'ver formatos' }}</f7-link>
              </div>
              <!-- La IA solo puede proponer UNA clasificación por formato. Cuando un formato es
                   genérico y en realidad pertenece a varias de las que ya existen —el acta general
                   de baja tensión sirve igual para una piscina que para un quirófano, y de
                   distinguirlos ya se encarga el discriminante— no tiene forma de decirlo y acaba
                   proponiendo un cajón nuevo. Desde aquí se saca de ahí y se manda a las que ya hay. -->
              <div v-if="nuevaAbierta === c._idx" class="rp-grupo-detalle">
                <div v-if="(c.formatos || []).length" class="destino-aviso">
                  ¿Alguno encaja en clasificaciones que ya existen? Sácalo con «a existentes» y elige sus destinos arriba.
                  <a class="link" style="margin-left:6px;" @click="moverTodosAExistentes(c)">mover todos</a>
                </div>
                <span v-for="(ff, fi) in (c.formatos || [])" :key="'cnf-'+c._idx+'-'+fi" class="rp-caract" :title="ff.nombre || ''">
                  {{ ff.codigo }}<span v-if="ff.nombre" class="rp-caract-tipo">{{ ff.nombre }}</span>
                  <span v-if="ff.expedientes" class="rp-caract-tipo">{{ ff.expedientes }} exp</span>
                  <a class="link rp-mover" @click.stop="moverAExistentes(c, fi)">a existentes</a>
                </span>
              </div>
            </div>
          </div>
        </template>

        <!-- ===================== Vista FORMATOS (per-formato, propuesta detallada) ===================== -->
        <template v-else>
        <div class="revision-toolbar">
          <input class="revision-filtro" type="search" placeholder="Filtrar (código, nombre, materia)…" v-model="revisionFiltro" />
          <span class="text-color-gray">{{ revisionFormatosVisibles.length }} / {{ revisionFormatos.length }} de {{ revisionTotal }} formatos</span>
          <f7-button v-if="revisionFormatos.length < revisionTotal" small @click="cargarPaginaRevision(revisionPage + 1)" :disabled="revisionCargando">Cargar más</f7-button>
          <f7-button v-if="!mostrarDescartados" small @click="descartarNoSinClasificar" title="Descartar los que no estén como «sin clasificar»">Materia Con Clasif.</f7-button>
          <f7-button small :fill="mostrarDescartados" :color="mostrarDescartados ? 'gray' : ''" @click="mostrarDescartados = !mostrarDescartados">
            {{ mostrarDescartados ? 'Ver activos' : 'Descartados (' + formatosDescartados.length + ')' }}
          </f7-button>
          <f7-button v-if="mostrarDescartados && formatosDescartados.length" small color="red" @click="limpiarDescartados">Restaurar todos</f7-button>
          <f7-button small fill color="green" @click="crearSeleccionadas" :disabled="creando || numSeleccionadas === 0">
            Crear seleccionadas ({{ numSeleccionadas }})
          </f7-button>
        </div>

        <div v-if="revisionCargando && !revisionFormatos.length" class="text-color-gray padding">Cargando formatos…</div>

        <div class="revision-lista">
          <div v-if="!revisionCargando && !revisionFormatosVisibles.length" class="text-color-gray padding">
            {{ mostrarDescartados ? 'No hay formatos descartados.' : (revisionFiltro ? 'Sin resultados para el filtro.' : 'No hay formatos.') }}
          </div>
          <div v-for="f in revisionFormatosVisibles" :key="'rf-' + f.codigo" class="revision-card" :class="{'revision-card-nueva': prop(f.codigo) && !prop(f.codigo).yaClasificado, 'revision-card-descartada': mostrarDescartados}">
            <!-- Indicador "pensando" centrado sobre la propia card mientras la IA redacta su propuesta -->
            <div v-if="proponiendoCodigo === f.codigo" class="card-brain-overlay">
              <svg class="brain-svg brain-svg-card" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                <path class="brain-path" d="M184 0c30.9 0 56 25.1 56 56V456c0 30.9-25.1 56-56 56c-28.9 0-52.7-21.9-55.7-50.1c-5.2 1.4-10.7 2.1-16.3 2.1c-35.3 0-64-28.7-64-64c0-7.4 1.3-14.6 3.6-21.2C21.4 367.4 0 338.2 0 304c0-31.9 18.7-59.5 45.8-72.3C37.1 220.8 32 207 32 192c0-30.7 21.6-56.3 50.4-62.6C80.8 123.9 80 118 80 112c0-29.9 20.6-55.1 48.3-62.1C131.3 21.9 155.1 0 184 0zM328 0c28.9 0 52.6 21.9 55.7 49.9C411.4 56.9 432 82.1 432 112c0 6-.8 11.9-2.4 17.4C458.4 135.7 480 161.3 480 192c0 15-5.1 28.8-13.8 39.7C493.3 244.5 512 272.1 512 304c0 34.2-21.4 63.4-51.6 74.8c2.3 6.6 3.6 13.8 3.6 21.2c0 35.3-28.7 64-64 64c-5.6 0-11.1-.7-16.3-2.1c-3 28.2-26.8 50.1-55.7 50.1c-30.9 0-56-25.1-56-56V56c0-30.9 25.1-56 56-56z"/>
                <g class="brain-sparks">
                  <circle cx="150" cy="170" r="20" class="spark spark-travel-a"/>
                  <circle cx="330" cy="150" r="20" class="spark"/>
                  <circle cx="200" cy="300" r="20" class="spark spark-travel-b"/>
                  <circle cx="360" cy="320" r="20" class="spark"/>
                  <circle cx="256" cy="410" r="20" class="spark"/>
                </g>
              </svg>
              <span class="brain-text ia-text brain-text-card">Pensando…</span>
            </div>
            <div class="revision-head">
              <label class="revision-check" v-if="prop(f.codigo) && !prop(f.codigo).yaClasificado">
                <input type="checkbox" v-model="prop(f.codigo)._seleccionada" />
              </label>
              <code class="revision-codigo">{{ f.codigo }}</code>
              <span class="text-color-gray">{{ f.nombre }}</span>
              <span class="badge color-blue" style="font-size:10px;">{{ f.materiaCodigo || '—' }}</span>
              <span class="text-color-gray" style="font-size:11px;">{{ (f.nombresCaracteristicas || []).length }} caract.</span>
              <!-- Estado -->
              <span v-if="!prop(f.codigo) && f.yaClasificadoMateria" class="badge color-teal estado-badge">materia con clasif.</span>
              <span v-else-if="!prop(f.codigo)" class="badge color-orange estado-badge">sin clasificar</span>
              <span v-else-if="prop(f.codigo).yaClasificado" class="badge color-teal estado-badge">ya clasificado</span>
              <span v-else class="badge color-purple estado-badge">propuesta nueva</span>
              <f7-button v-if="!prop(f.codigo) && !mostrarDescartados && !enCola(f.codigo) && proponiendoCodigo !== f.codigo" small @click="encolarProponer(f.codigo)" class="revision-btn-proponer ia-text">Proponer</f7-button>
              <f7-link v-else-if="!prop(f.codigo) && !mostrarDescartados && enCola(f.codigo)" class="revision-btn-proponer revision-btn-pendiente" @click="quitarDeCola(f.codigo)" title="En cola — clic para quitar">pendiente ✕</f7-link>
              <f7-link v-if="!mostrarDescartados" class="revision-btn-descartar text-color-gray" @click="descartar(f.codigo)" title="Quitar de la vista (no se usará para clasificar)">Descartar</f7-link>
              <f7-link v-else class="revision-btn-descartar text-color-blue" @click="restaurar(f.codigo)" title="Volver a mostrar">Restaurar</f7-link>
            </div>

            <!-- Propuesta (editable) -->
            <div v-if="prop(f.codigo) && !prop(f.codigo).yaClasificado && prop(f.codigo).propuesta" class="revision-propuesta">
              <div class="rp-row">
                <label>Tipo</label>
                <input class="rp-input" v-model="prop(f.codigo).propuesta.tipo" />
                <label>Subtipos (coma)</label>
                <input class="rp-input rp-input-wide" :value="(prop(f.codigo).propuesta.subtipos || []).join(', ')" @input="setSubtipos(f.codigo, $event.target.value)" />
                <label>Naturaleza</label>
                <select class="rp-input" v-model="prop(f.codigo).propuesta.naturaleza">
                  <option value="FIJ">FIJ</option>
                  <option value="MOV">MOV</option>
                </select>
              </div>
              <div class="rp-row">
                <label>Plantilla ID</label>
                <input class="rp-input rp-input-full" v-model="prop(f.codigo).propuesta.plantillaIdItem" />
              </div>
              <div class="rp-grupos">
                <span v-for="(g, gi) in (prop(f.codigo).propuesta.grupos || [])" :key="'rg-'+f.codigo+'-'+gi"
                  class="rp-grupo" :class="{'rp-grupo-activo': grupoAbiertoEs(f.codigo, gi)}"
                  @click="toggleGrupoPropuesta(f.codigo, gi)"
                  title="Ver características del grupo">
                  {{ g.codigo }} ({{ (g.caracteristicas || []).length }})
                </span>
              </div>
              <div v-if="grupoActivo(f.codigo)" class="rp-grupo-detalle">
                <span class="rp-grupo-detalle-titulo">{{ grupoActivo(f.codigo).denominacion || grupoActivo(f.codigo).codigo }}</span>
                <span v-for="(c, ci) in (grupoActivo(f.codigo).caracteristicas || [])" :key="'rgc-'+f.codigo+'-'+ci" class="rp-caract" :title="(c.aliases || []).length ? ('alias: ' + (c.aliases || []).join(', ')) : ''">
                  {{ c.nombre }}<span v-if="c.tipo" class="rp-caract-tipo">{{ c.tipo }}</span>
                </span>
                <span v-if="!(grupoActivo(f.codigo).caracteristicas || []).length" class="text-color-gray" style="font-size:11px;">— sin características —</span>
              </div>
              <div v-if="prop(f.codigo).observaciones" class="rp-obs">{{ prop(f.codigo).observaciones }}</div>
            </div>
            <div v-else-if="prop(f.codigo) && prop(f.codigo).yaClasificado" class="revision-yaclasif">
              Ya clasificado como <strong>{{ prop(f.codigo).clasificacionExistente && prop(f.codigo).clasificacionExistente.tipo }}
              {{ prop(f.codigo).clasificacionExistente && (prop(f.codigo).clasificacionExistente.subtipos || []).join(' / ') }}</strong>
            </div>
          </div>
        </div>
        </template>
      </f7-page>
    </f7-popup>

    <!-- Popup: fusionar clasificaciones (manual) -->
    <f7-popup class="popup-fusion-clasif" v-if="fusionPopupOpened" :opened="true" @popup:closed="cerrarFusionPopup">
      <f7-page>
        <f7-navbar>
          <f7-nav-title>Fusionar clasificaciones</f7-nav-title>
          <f7-nav-right><f7-link popup-close>Cerrar</f7-link></f7-nav-right>
        </f7-navbar>

        <div class="fusion-toolbar">
          <div style="flex:1 1 auto; font-size:12px;">
            <span class="text-color-gray">Destino:</span>
            <select v-model="fusionIdDestino" @change="cargarFusionPreview" :disabled="fusionCargando || fusionAplicando" class="fusion-select">
              <option v-for="s in fusionSeleccionadas" :key="s.id" :value="s.id">{{ etiquetaClasif(s) }}</option>
            </select>
            <span class="text-color-gray" style="margin-left:8px;">absorbe a {{ fusionSeleccionadas.length - 1 }} más</span>
          </div>
          <f7-button small fill color="purple" @click="aplicarFusion" :disabled="fusionCargando || fusionAplicando || !fusionPreview">
            {{ fusionAplicando ? 'Fusionando…' : 'Fusionar' }}
          </f7-button>
        </div>

        <r-ia-pensando v-if="fusionCargando" :narracion="fusionNarracion" latin-default="Comparing" texto-default="Comparando características…" />

        <div v-else-if="fusionPreview" class="fusion-cuerpo">
          <div class="fusion-bloque">
            <div class="fusion-bloque-tit">Formatos a añadir ({{ fusionPreview.formatosAAnadir.length }})</div>
            <div v-if="fusionPreview.formatosAAnadir.length" class="fusion-chips">
              <span v-for="cod in fusionPreview.formatosAAnadir" :key="cod" class="badge color-gray fusion-chip">{{ cod }}</span>
            </div>
            <div v-else class="text-color-gray" style="font-size:12px;">— ninguno nuevo —</div>
          </div>

          <div class="fusion-bloque">
            <div class="fusion-bloque-tit">Se añadirán como alias ({{ fusionPreview.emparejadas.length }})</div>
            <div v-if="fusionPreview.emparejadas.length">
              <div v-for="(e, ei) in fusionPreview.emparejadas" :key="'emp-'+ei" class="fusion-empareja">
                <span v-for="(a, ai) in e.aliasNuevos" :key="ai" class="badge color-orange fusion-chip">{{ a }}</span>
                <i class="f7-icons" style="font-size:13px;">arrow_right</i>
                <code>{{ e.canonico }}</code>
                <span class="text-color-gray" style="font-size:11px;">({{ e.grupoCodigo }})</span>
              </div>
            </div>
            <div v-else class="text-color-gray" style="font-size:12px;">— ninguna —</div>
          </div>

          <div class="fusion-bloque">
            <div class="fusion-bloque-tit">Sin emparejar — decide qué hacer ({{ fusionPreview.noEmparejadas.length }})</div>
            <table v-if="fusionPreview.noEmparejadas.length" class="fusion-tabla">
              <thead><tr><th>Característica</th><th>Origen</th><th>Acción</th><th>Destino</th></tr></thead>
              <tbody>
                <tr v-for="(ne, ni) in fusionPreview.noEmparejadas" :key="'ne-'+ni">
                  <td><code>{{ ne.nombre }}</code><span v-if="ne.label" class="text-color-gray"> · {{ ne.label }}</span></td>
                  <td class="text-color-gray" style="font-size:11px;">{{ ne.origenClasif }} / {{ ne.origenGrupo }}</td>
                  <td>
                    <select v-model="fusionResoluciones[ni].accion" class="fusion-select">
                      <option value="ELIMINAR">Eliminar</option>
                      <option value="GRUPO_EXISTENTE">A grupo existente</option>
                      <option value="GRUPO_NUEVO">A grupo nuevo</option>
                    </select>
                  </td>
                  <td>
                    <select v-if="fusionResoluciones[ni].accion === 'GRUPO_EXISTENTE'" v-model="fusionResoluciones[ni].grupoCodigo" class="fusion-select">
                      <option v-for="g in fusionPreview.gruposDestino" :key="g.codigo" :value="g.codigo">{{ g.denominacion || g.codigo }}</option>
                    </select>
                    <input v-else-if="fusionResoluciones[ni].accion === 'GRUPO_NUEVO'" v-model="fusionResoluciones[ni].grupoNuevoDenominacion" placeholder="Nombre del grupo nuevo" class="fusion-input" />
                    <span v-else class="text-color-gray" style="font-size:11px;">se descarta</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-else class="text-color-gray" style="font-size:12px;">— ninguna, todo casó —</div>
          </div>
        </div>
      </f7-page>
    </f7-popup>

    <!-- ============ Generar items enriquecidos ============ -->
    <f7-popup class="popup-generar-items" v-if="generarPopupOpened" :opened="true" @popup:closed="cerrarGenerarPopup">
      <f7-page>
        <f7-navbar title="Generar items enriquecidos">
          <f7-nav-right><f7-link popup-close>Cerrar</f7-link></f7-nav-right>
        </f7-navbar>

        <div class="generar-items-contenido" style="padding: 12px;">
          <p class="text-color-gray" style="margin-top:0;">
            Genera (o re-sincroniza) items enriquecidos a partir de los expedientes que cumplan las condiciones.
            <a class="link" style="font-size:13px;" @click="generarAyudaVisible = !generarAyudaVisible">
              {{ generarAyudaVisible ? 'Ocultar ayuda' : '¿Cómo funciona el filtro?' }}
            </a>
          </p>

          <div v-if="generarAyudaVisible" class="generar-ayuda">
            <ul>
              <li>Cada línea es una condición: <strong>campo + operador + valor(es)</strong>. Añade tantas como necesites y elimínalas con el botón rojo.</li>
              <li>El selector superior decide si deben cumplirse <strong>TODAS (AND)</strong> o basta <strong>CUALQUIERA (OR)</strong>.</li>
              <li><strong>Campo</strong>: escribe para filtrar la lista (autocompletado); son los campos del expediente, los mismos del editor de plantillas de informes.</li>
              <li><strong>Operadores según el tipo</strong>: fechas → <em>entre</em> (dos fechas), <em>≥</em>, <em>≤</em>; números → <em>=, ≥, ≤, entre</em>; textos → <em>=, contiene, en / no en</em> (varios valores separados por comas); desplegables (p. ej. estado) → <em>en / no en</em> con selección múltiple; y <em>vacío / relleno</em> para campos sin informar o informados (no piden valor).</li>
              <li>Al entrar (o si borras todas las condiciones) se proponen dos por defecto: <strong>fecha de inspección del último mes</strong> y <strong>estado ENVIADO</strong>. No se puede generar sin condiciones.</li>
              <li><strong>Solo los que no tienen item</strong>: deja fuera los expedientes que ya generaron uno. Se aplica siempre en AND con el resto. Es lo que hay que marcar en barridos grandes: cada expediente procesado sale del conjunto, así que si cancelas y relanzas no se repite lo ya hecho.</li>
              <li>El contador muestra cuántos expedientes se procesarán. Generar es <strong>re-ejecutable</strong>: si un expediente ya tenía item, lo actualiza (no duplica). Puedes <strong>cancelar</strong> durante el proceso y al final verás el resumen con los fallos y su motivo.</li>
            </ul>
          </div>

          <div class="display-flex align-items-center" style="gap:8px; margin-bottom:8px;">
            <span>Cuando se cumplan</span>
            <select v-model="generarCombinador" @change="schedulePreview" class="input-select-nativo">
              <option value="AND">TODAS (AND)</option>
              <option value="OR">CUALQUIERA (OR)</option>
            </select>
            <span>de las condiciones:</span>
            <label class="display-flex align-items-center" style="gap:6px; margin-left:auto; cursor:pointer;">
              <input type="checkbox" v-model="generarSoloSinItem" @change="schedulePreview" />
              <span>Solo los que no tienen item</span>
            </label>
          </div>

          <div v-if="!generarCampos.length" class="text-color-gray">Cargando campos…</div>

          <div class="cond-list">
          <div v-for="(cond, i) in generarCondiciones" :key="'cond-'+i" class="cond-row">
            <!-- Campo (autocomplete: escribe para filtrar) -->
            <input type="text" list="generar-campos-datalist" class="input-texto cond-campo"
                   placeholder="Campo… (escribe para filtrar)"
                   :value="labelDe(cond)" @change="onCampoInput(cond, $event.target.value)" />
            <!-- Operador -->
            <select v-model="cond.operador" @change="onOperadorChange(cond)" class="input-select-nativo cond-op" :disabled="!cond.campo">
              <option v-for="op in operadoresDe(cond)" :key="op" :value="op">{{ etiquetaOperador(op) }}</option>
            </select>
            <!-- Valor(es): columna propia, reparte el ancho restante -->
            <div class="cond-val" v-if="cond.campo && cond.operador">
              <template v-if="necesitaValor(cond)">
                <!-- ENUM: multiselect -->
                <select v-if="tipoDe(cond) === 'ENUM'" multiple v-model="cond.valores" @change="schedulePreview"
                        class="input-select-nativo">
                  <option v-for="o in opcionesDe(cond)" :key="o.valor" :value="o.valor">{{ o.label }}</option>
                </select>
                <!-- BETWEEN: dos inputs -->
                <template v-else-if="cond.operador === 'BETWEEN'">
                  <input :type="inputType(cond)" :value="cond.valores[0]" @input="setVal(cond,0,$event.target.value)" class="input-texto" placeholder="desde" />
                  <input :type="inputType(cond)" :value="cond.valores[1]" @input="setVal(cond,1,$event.target.value)" class="input-texto" placeholder="hasta" />
                </template>
                <!-- IN/NOT_IN/IN_IGNORE_CASE sobre texto: separados por comas -->
                <input v-else-if="cond.operador === 'IN' || cond.operador === 'NOT_IN' || cond.operador === 'IN_IGNORE_CASE'"
                       type="text" :value="cond.valores[0]" @input="setVal(cond,0,$event.target.value)"
                       class="input-texto" placeholder="valores separados por comas" />
                <!-- EXISTS (catálogos antiguos): SÍ/NO en vez de true/false -->
                <select v-else-if="cond.operador === 'EXISTS'" :value="cond.valores[0] || 'true'"
                        @change="setVal(cond,0,$event.target.value)" class="input-select-nativo">
                  <option value="true">SÍ</option>
                  <option value="false">NO</option>
                </select>
                <!-- resto: un valor -->
                <input v-else :type="inputType(cond)" :value="cond.valores[0]" @input="setVal(cond,0,$event.target.value)"
                       class="input-texto" placeholder="valor" />
              </template>
              <span v-else class="text-color-gray" style="font-size:12px;">(sin valor)</span>
            </div>
            <f7-link icon-f7="minus_circle_fill" color="red" @click="removeCondicion(i)"></f7-link>
          </div>
          </div>

          <!-- Opciones del autocomplete de campos (compartido por todas las filas). -->
          <datalist id="generar-campos-datalist">
            <option v-for="c in generarCampos" :key="'dl-'+c.clave" :value="c._display"></option>
          </datalist>

          <f7-link class="margin-vertical" icon-f7="plus_circle" @click="addCondicion">Añadir condición</f7-link>

          <div class="generar-preview" style="margin-top:12px; padding:8px; background:#f2f4f7; border-radius:8px;">
            <span v-if="generarPreviewCargando">Contando expedientes…</span>
            <span v-else-if="generarPreviewTotal !== null">
              Se procesarán <strong>{{ generarPreviewTotal }}</strong> expediente(s).
            </span>
            <span v-else class="text-color-gray">Define condiciones para ver el conteo.</span>
          </div>

          <r-ia-pensando v-if="generarLanzando" :narracion="generarNarracion" latin-default="Generating" texto-default="Generando items enriquecidos…" />

          <div class="display-flex" style="gap:8px; margin-top:14px;">
            <f7-button fill color="blue"
              :disabled="generarLanzando || generarPreviewCargando || !generarPreviewTotal" @click="lanzarGeneracion">
              {{ generarPreviewCargando ? 'Contando…' : 'Generar ' + (generarPreviewTotal || 0) + ' items' }}
            </f7-button>
            <f7-button v-if="generarLanzando" outline color="red" @click="cancelarGeneracion">Cancelar</f7-button>
          </div>
        </div>
      </f7-page>
    </f7-popup>
  </f7-page>
</template>

<script>
import rDynamicList from "./../../components/rDynamicList.vue";
import rIaPensando from "./../../components/rIaPensando.vue";
import ItemClasificacion from "./../../js/db/ItemClasificacion.js";
import RevisionFormatosEvents from "./../../js/db/RevisionFormatosEvents.js";
import GeneracionItems from "./../../js/db/GeneracionItems.js";
import GeneracionItemsEvents from "./../../js/db/GeneracionItemsEvents.js";
import ItemTipoClasificacion from "./../../js/db/ItemTipoClasificacion.js";
import NaturalezasMaestro from "./../../js/utils/naturalezasMaestro.js";
import preloaderDeLaSesion from "./../../js/utils/preloaderDeLaSesion.js";

export default {
  components: {
    rDynamicList,
    rIaPensando
  },
  data() {
    return {
      ItemClasificacion,
      encodeURIComponent,
      coloresPorCodigo: {},
      // Condiciones de discriminación entre subtipos (IA)
      OPERADORES: ['IN', 'IGUAL', 'NOT_IN', 'CONTIENE', 'MAYOR', 'MAYOR_IGUAL', 'MENOR', 'MENOR_IGUAL', 'ENTRE', 'EXISTE'],
      tiposMaestro: [],
      discPopupOpened: false,
      discCodigo: '',
      discCargando: false,
      discGuardando: false,
      discNarracion: null,
      discResultado: null,
      discPropuestas: [],
      discEmpates: [],
      // Revisión de formatos con IA
      revisionPopupOpened: false,
      revisionCargando: false,
      revisionFormatos: [],
      revisionPage: 0,
      revisionSize: 100,
      revisionTotal: 0,
      // codigo formato → { propuesta, yaClasificado, clasificacionExistente, observaciones, confianza, materiaCodigo, _seleccionada }
      propuestasMap: {},
      proponiendo: false,
      proponiendoCodigo: null,
      // Tanda de cálculo de propuestas de características (varias clasificaciones a la vez).
      sugerenciaEnCurso: false,
      sugerenciaCancelando: false,
      sugerenciaAvance: null,
      _sondeoSugerencia: null,
      colaProponer: [],
      progreso: { hechos: 0, total: 0 },
      creando: false,
      // Formatos descartados de la revisión (no se usarán para clasificar). Persistido en localStorage.
      formatosDescartados: [],
      mostrarDescartados: false,
      revisionFiltro: '',
      // Grupo de propuesta cuyo detalle de características está desplegado ("codigo|gi").
      grupoAbierto: null,
      // --- Detección en lote ---
      vistaDeteccion: true,
      deteccionCargando: false,
      clasifNuevas: [],
      /** Una fila por formato que encaja en una clasificación existente, con su destino editable. */
      filasVincular: [],
      /** Clasificaciones disponibles por materia, para poder corregir el destino. */
      clasifPorMateria: {},
      vinculando: false,
      deteccionInfo: null,
      nuevaAbierta: null,
      narracion: null,
      narracionCreacion: null,
      creacionCancelando: false,
      deteccionFiltro: '',
      // Filtro de materias para la detección (vacío = todas).
      materiasPendientesLista: [],
      materiasFiltro: [],
      // --- Fusión manual de clasificaciones ---
      fusionPopupOpened: false,
      fusionCargando: false,
      fusionAplicando: false,
      fusionNarracion: null,
      fusionSeleccionadas: [],   // [{id, codigo, subtipos, denominacion}]
      fusionIdDestino: '',
      fusionPreview: null,       // {formatosAAnadir, emparejadas, noEmparejadas, gruposDestino}
      fusionResoluciones: [],    // por cada noEmparejada: {accion, grupoCodigo, grupoNuevoDenominacion}
      // --- Generar items enriquecidos (filtro dinámico) ---
      generarPopupOpened: false,
      generarAyudaVisible: false,
      generarCampos: [],         // catálogo [{clave,label,tipo,operadores[],opciones[]}]
      generarCamposMap: {},      // clave → campo
      generarCombinador: 'AND',
      generarCondiciones: [],    // [{campo, operador, valores:[]}]
      generarSoloSinItem: false, // deja fuera los expedientes que ya tienen idItemEnriquecido
      generarPreviewTotal: null,
      generarPreviewCargando: false,
      generarLanzando: false,
      generarNarracion: null
    };
  },
  computed: {
    // Formatos visibles según el modo (normal = no descartados; modo descartados = solo descartados)
    // y el filtro de texto libre (código, nombre o materia).
    revisionFormatosVisibles() {
      var self = this;
      var q = (this.revisionFiltro || '').trim().toLowerCase();
      return this.revisionFormatos.filter(function (f) {
        var desc = self.formatosDescartados.indexOf(f.codigo) >= 0;
        if (self.mostrarDescartados ? !desc : desc) return false;
        if (!q) return true;
        var txt = ((f.codigo || '') + ' ' + (f.nombre || '') + ' ' + (f.materiaCodigo || '')).toLowerCase();
        return txt.indexOf(q) >= 0;
      });
    },
    hayPendientes() {
      var self = this;
      return this.revisionFormatos.some(f => !this.propuestasMap[f.codigo] && self.formatosDescartados.indexOf(f.codigo) < 0);
    },
    numSeleccionadas() {
      var n = 0;
      var self = this;
      Object.keys(this.propuestasMap).forEach(function (k) {
        var p = self.propuestasMap[k];
        if (p && p._seleccionada && p.propuesta && !p.yaClasificado) n++;
      });
      return n;
    },
    numNuevasSel() {
      // Solo las VISIBLES marcadas: si filtras, "Crear seleccionadas" no debe arrastrar las
      // que quedan ocultas aunque sigan marcadas por detrás.
      return this.nuevasParaCrear.length;
    },
    // Sugerencias que se crearán: visibles según el filtro Y marcadas.
    nuevasParaCrear() {
      return this.clasifNuevasVisibles.filter(function (c) { return c._sel; });
    },
    /** Formatos a vincular visibles según el mismo filtro de texto que las sugerencias nuevas. */
    filasVincularVisibles() {
      var q = (this.deteccionFiltro || '').trim().toLowerCase();
      if (!q) return this.filasVincular;
      return this.filasVincular.filter(function (f) {
        return [f.codigo, f.nombre, f.materiaCodigo].join(' ').toLowerCase().indexOf(q) >= 0;
      });
    },
    /** Marcadas Y con algún destino: sin destino no hay nada que aplicar. */
    filasParaVincular() {
      return this.filasVincularVisibles.filter(function (f) { return f._sel && f.destinos.length; });
    },
    numExistentesSel() {
      return this.filasParaVincular.length;
    },
    /** Total de formatos que irían a clasificaciones existentes (para la cabecera). */
    formatosEnExistentes() {
      return this.filasVincular.length;
    },
    // Sugerencias visibles según el filtro de texto (coincide en tipo, subtipos, naturaleza,
    // materias o cualquier formato —código o nombre—).
    clasifNuevasVisibles() {
      var q = (this.deteccionFiltro || '').trim().toLowerCase();
      if (!q) return this.clasifNuevas;
      return this.clasifNuevas.filter(function (c) {
        var partes = [c.tipo, c.naturaleza]
          .concat(c.subtipos || [])
          .concat(c.materiasCodigos || [])
          .concat((c.formatos || []).map(function (f) { return (f.codigo || '') + ' ' + (f.nombre || ''); }));
        return partes.join(' ').toLowerCase().indexOf(q) >= 0;
      });
    },
    todasVisiblesMarcadas() {
      var vis = this.clasifNuevasVisibles;
      return vis.length > 0 && vis.every(function (c) { return c._sel; });
    }
  },
  mounted() {
    this.aplicarTiposDelMaestro();
    this.aplicarNaturalezasDelMaestro();
    this.cargarDescartados();
  },
  // Si la ruta usa keepAlive, mounted solo se ejecuta una vez; activated cubre el reingreso.
  activated() {
    // Al volver puede haber una tanda de propuestas corriendo desde antes (o desde otra pestaña):
    // se retoma el sondeo para no dejar el botón habilitado encima de un cálculo en curso.
    this.retomarSugerenciaEnCurso();
  },
  beforeDestroy() {
    this.pararSondeoSugerencia();
  },
  deactivated() {
    this.pararSondeoSugerencia();
  },
  methods: {
    estiloSubtipo(nivel) {
      // Tamaño decreciente por profundidad: subtipo1 11px, subtipo2 10px, subtipo3 9px... (mín 8px).
      var fontSize = Math.max(11 - nivel, 8);
      var padV = Math.max(2 - nivel, 0);
      var padH = Math.max(8 - nivel * 2, 4);
      return {
        padding: padV + 'px ' + padH + 'px',
        fontSize: fontSize + 'px',
        fontWeight: '500',
        whiteSpace: 'nowrap'
      };
    },
    // --- Condiciones de discriminación entre subtipos (IA) ---
    abrirDiscriminantes() {
      this.discPopupOpened = true;
    },
    cerrarDiscriminantes() {
      this.discPopupOpened = false;
      if (this._discHandler) RevisionFormatosEvents.unsubscribe(this._discHandler);
    },
    esOperadorNumerico(op) {
      return ['MAYOR', 'MAYOR_IGUAL', 'MENOR', 'MENOR_IGUAL', 'ENTRE'].indexOf(op) >= 0;
    },
    cargarEmpates() {
      const self = this;
      self.discEmpates = [];
      if (!self.discCodigo) return;
      ItemClasificacion.empatesDiscriminantes(self.discCodigo).then(function (res) {
        self.discEmpates = (res && res.empates) || [];
      }).catch(function () { /* informativo: si falla no molestamos */ });
    },
    /**
     * Opciones declaradas de una característica (discriminantes), unidas de todos sus formatos.
     * OJO: nombre único a propósito — ya existe otra `opcionesDe(cond)` en este componente para
     * otra funcionalidad, y en `methods` la última definición gana. No renombrar a `opcionesDe`.
     */
    opcionesDeclaradas(nombre) {
      var cats = (this.discResultado && this.discResultado.caracteristicas) || [];
      var clave = (nombre || '').trim();
      var cat = cats.find(function (c) { return (c.nombre || '').trim() === clave; });
      if (!cat) return [];
      var out = [];
      (cat.opcionesPorFormato || []).forEach(function (of) {
        (of.opciones || []).forEach(function (o) { if (out.indexOf(o) < 0) out.push(o); });
      });
      return out;
    },
    /** Opciones declaradas que aún no están elegidas en esta condición (para el desplegable de añadir). */
    opcionesParaAnadir(cond) {
      var ya = cond.valores || [];
      return this.opcionesDeclaradas(cond.caracteristica).filter(function (o) { return ya.indexOf(o) < 0; });
    },
    etiquetaOperador(op) {
      var m = {
        IN: 'contiene alguno de', IGUAL: 'es igual a', NOT_IN: 'no es ninguno de',
        CONTIENE: 'contiene', MAYOR: 'es mayor que', MAYOR_IGUAL: 'es mayor o igual que',
        MENOR: 'es menor que', MENOR_IGUAL: 'es menor o igual que', ENTRE: 'está entre', EXISTE: 'está relleno'
      };
      return m[op] || op;
    },
    ayudaOperador(op) {
      if (['MAYOR', 'MAYOR_IGUAL', 'MENOR', 'MENOR_IGUAL', 'ENTRE'].indexOf(op) >= 0) return 'Compara el número del campo con el valor indicado.';
      if (op === 'EXISTE') return 'Se cumple si el campo tiene cualquier valor.';
      if (op === 'CONTIENE' || op === 'IN') return 'Se cumple si el valor del campo incluye alguno de los valores listados (para campos de varias opciones).';
      if (op === 'NOT_IN') return 'Se cumple si el valor NO es ninguno de los listados.';
      return 'Se cumple si el valor del campo coincide con el indicado.';
    },
    quitarValor(cond, indice) {
      if (cond.valores) cond.valores.splice(indice, 1);
    },
    anadirValorSelect(cond, ev) {
      var v = ev && ev.target ? ev.target.value : '';
      if (!v) return;
      if (!cond.valores) this.$set(cond, 'valores', []);
      if (cond.valores.indexOf(v) < 0) cond.valores.push(v);
      ev.target.value = ''; // deja el desplegable en el placeholder
    },
    anadirValorTexto(cond, ev) {
      var v = ev && ev.target ? (ev.target.value || '').trim() : '';
      if (!v) return;
      if (!cond.valores) this.$set(cond, 'valores', []);
      if (cond.valores.indexOf(v) < 0) cond.valores.push(v);
      ev.target.value = '';
    },
    anadirCondicion(propuesta) {
      var cats = (this.discResultado && this.discResultado.caracteristicas) || [];
      propuesta.condiciones.push({
        caracteristica: cats.length ? cats[0].nombre : '',
        operador: 'IN',
        valores: [],
        valorNumerico: null,
        valorNumericoHasta: null,
        unidad: null,
        origen: 'MANUAL',
        justificacion: ''
      });
    },
    revisarDiscriminantes() {
      const self = this;
      if (!self.discCodigo) return;
      self.discCargando = true;
      self.discResultado = null;
      self.discPropuestas = [];
      self.discNarracion = { latin: 'Starting', texto: 'Lanzando la revisión…', hechos: 0, total: 0 };
      if (!self._discHandler) self._discHandler = self._onDiscEvent.bind(self);
      // Igual que en la detección: lanzar primero y suscribirse después, para que el reenvío
      // del último evento corresponda a este run y no a uno anterior.
      ItemClasificacion.revisarDiscriminantes(self.discCodigo).then(function (res) {
        RevisionFormatosEvents.subscribe(self._discHandler, self.$f7);
        if (res && res.lanzado === false) {
          self.discCargando = false;
          self.discNarracion = null;
          self.$f7.dialog.alert(res.error || 'Ya hay una revisión en curso.', 'No se pudo lanzar');
        }
      }).catch(function (err) {
        self.discCargando = false;
        self.discNarracion = null;
        self.$f7.dialog.alert(self._msgError(err), 'Error lanzando la revisión');
      });
    },
    _onDiscEvent(payload) {
      const self = this;
      if (!payload || typeof payload !== 'object') return;
      // El canal SSE es compartido con la revisión de formatos: solo nos interesan los nuestros.
      if (payload.accion !== 'discriminantes') return;
      // Y con las revisiones de UNA clasificación lanzadas desde su ficha: esas no son nuestras.
      if (payload.idDiana) return;

      if (payload.estado === 'progreso') {
        self.discNarracion = { latin: payload.latin, texto: payload.texto, hechos: payload.hechos, total: payload.total };
        return;
      }
      self.discCargando = false;
      self.discNarracion = null;
      RevisionFormatosEvents.unsubscribe(self._discHandler);

      if (payload.estado === 'error') {
        self.$f7.dialog.alert(payload.texto || 'Error revisando las condiciones.', 'Error');
        return;
      }
      if (payload.estado === 'cancelado') {
        self.$f7.toast.create({ text: 'Revisión cancelada.', closeTimeout: 2500 }).open();
        return;
      }
      var r = payload.resultado || {};
      self.discResultado = r;
      self.discPropuestas = (r.propuestas || []).map(function (p) {
        return {
          clasificacionId: p.clasificacionId,
          subtipos: p.subtipos || [],
          confianza: p.confianza,
          condiciones: (p.condiciones || []).map(function (c) {
            return {
              caracteristica: c.caracteristica,
              operador: c.operador || 'IN',
              valores: c.valores || [],
              valorNumerico: c.valorNumerico != null ? c.valorNumerico : null,
              valorNumericoHasta: c.valorNumericoHasta != null ? c.valorNumericoHasta : null,
              unidad: c.unidad || null,
              origen: 'IA',
              justificacion: c.justificacion || ''
            };
          })
        };
      });
      if (!self.discPropuestas.length) {
        self.$f7.toast.create({ text: 'La IA no ha propuesto condiciones.', closeTimeout: 3000 }).open();
      }
    },
    cancelarDiscriminantes() {
      const self = this;
      ItemClasificacion.cancelarDiscriminantes().catch(function () { /* best-effort */ });
      self.$f7.toast.create({ text: 'Cancelando…', closeTimeout: 2000 }).open();
    },
    guardarDiscriminantes() {
      const self = this;
      self.discGuardando = true;
      var payload = self.discPropuestas.map(function (p) {
        return { clasificacionId: p.clasificacionId, subtipos: p.subtipos, condiciones: p.condiciones };
      });
      ItemClasificacion.confirmarDiscriminantes(payload).then(function (res) {
        self.discGuardando = false;
        var guardadas = (res && res.guardadas) || [];
        var errores = (res && res.errores) || [];
        self.$f7.toast.create({
          text: guardadas.length + ' clasificación(es) actualizada(s).' + (errores.length ? ' ' + errores.length + ' con error.' : ''),
          closeTimeout: 3500
        }).open();
        if (errores.length) self.$f7.dialog.alert(errores.join('\n'), 'Condiciones no guardadas');
      }).catch(function (err) {
        self.discGuardando = false;
        self.$f7.dialog.alert(self._msgError(err), 'Error guardando las condiciones');
      });
    },
    // --- Revisión de formatos con IA ---
    prop(codigo) {
      return this.propuestasMap[codigo] || null;
    },
    // --- Detección en lote: agrupa formatos en clasificaciones nuevas ---
    detectar() {
      const self = this;
      self.deteccionCargando = true;
      self.clasifNuevas = [];
      self.deteccionInfo = null;
      self.nuevaAbierta = null;
      self.narracion = { latin: 'Starting', texto: 'Lanzando la detección…', hechos: 0, total: 0 };
      if (!self._deteccionHandler) self._deteccionHandler = self._onDeteccionEvent.bind(self);
      // Lanzar PRIMERO (fija el estado del run actual en el backend) y suscribirse DESPUÉS,
      // para que el reenvío del último evento corresponda a este run y no a uno anterior.
      ItemClasificacion.detectarClasificaciones(self.materiasFiltro).then(function (res) {
        RevisionFormatosEvents.subscribe(self._deteccionHandler, self.$f7);
        if (res && res.lanzado === false && res.enCurso) {
          self.$f7.toast.create({ text: 'Ya hay una detección en curso; te aviso al terminar.', closeTimeout: 3000 }).open();
        }
      }).catch(function (err) {
        self.deteccionCargando = false;
        self.narracion = null;
        self.$f7.dialog.alert(self._msgError(err), 'Error lanzando la detección');
      });
    },
    _onDeteccionEvent(payload) {
      const self = this;
      if (!payload || typeof payload !== 'object') return;
      // Eventos de la fase de CREACIÓN (montaje de tipologías).
      if (payload.accion === 'creacion') {
        if (payload.estado === 'progreso') {
          self.creando = true;
          self.narracionCreacion = { latin: payload.latin, texto: payload.texto, hechos: payload.hechos, total: payload.total };
        } else if (payload.estado === 'fin' || payload.estado === 'cancelado') {
          var cancelado = payload.estado === 'cancelado';
          self.creando = false;
          self.narracionCreacion = null;
          self.creacionCancelando = false;
          RevisionFormatosEvents.unsubscribe(self._deteccionHandler);
          var resumen = payload.resumen || {};
          var creadas = resumen.creadas || 0;
          var errores = resumen.errores || [];
          // Si terminó sin errores ni cancelación, quitamos de la lista SOLO las enviadas a crear
          // (las visibles marcadas al pulsar). Si se canceló, no quitamos nada (no sabemos cuáles
          // de las enviadas llegaron a crearse); una nueva detección las refrescará.
          if (!cancelado && !errores.length) {
            self.clasifNuevas = self.clasifNuevas.filter(function (c) { return !c._enCreacion; });
          } else {
            self.clasifNuevas.forEach(function (c) { c._enCreacion = false; });
          }
          // Algunas materias pueden haber quedado ya sin formatos pendientes: refrescamos el filtro.
          self.cargarMateriasPendientes();
          var msgFin = cancelado
            ? 'Creación cancelada. ' + creadas + ' clasificación(es) creada(s).'
            : creadas + ' clasificación(es) creada(s).' + (errores.length ? ' ' + errores.length + ' con error.' : '');
          self.$f7.toast.create({ text: msgFin, closeTimeout: 3500 }).open();
          if (errores.length) self.$f7.dialog.alert(errores.join('\n'), 'Errores al crear');
        } else if (payload.estado === 'error') {
          self.creando = false;
          self.narracionCreacion = null;
          RevisionFormatosEvents.unsubscribe(self._deteccionHandler);
          self.$f7.dialog.alert(payload.texto || 'Error creando clasificaciones.', payload.latin || 'Error');
        }
        return;
      }
      // Eventos de la fase de DETECCIÓN.
      if (payload.estado === 'progreso') {
        self.deteccionCargando = true;
        self.narracion = { latin: payload.latin, texto: payload.texto, hechos: payload.hechos, total: payload.total };
      } else if (payload.estado === 'fin') {
        var r = payload.resultado || {};
        var nuevas = r.nuevas || [];
        nuevas.forEach(function (c, i) { self.$set(c, '_sel', true); self.$set(c, '_idx', i); });
        self.clasifNuevas = nuevas;
        // Una fila por formato, con el destino que propone la IA ya marcado pero editable.
        // NO se marcan por defecto: vincular cambia la clasificación de los expedientes que ya
        // usan ese formato, así que se elige uno a uno.
        self.clasifPorMateria = r.clasificacionesPorMateria || {};
        var filas = [];
        (r.existentes || []).forEach(function (c) {
          (c.formatos || []).forEach(function (f) {
            filas.push({
              codigo: f.codigo,
              nombre: f.nombre || '',
              materiaCodigo: f.materiaCodigo || null,
              // Materias donde buscar destinos: la del formato y las de la clasificación que
              // propone la IA, que no tienen por qué coincidir (un ensayo DP va a una REAT).
              materiasDestino: [f.materiaCodigo].concat(c.materiasCodigos || []).filter(Boolean),
              expedientes: f.expedientes || 0,
              expedientesConItem: f.expedientesConItem || 0,
              propuesta: c.idClasificacion,
              destinos: c.idClasificacion ? [c.idClasificacion] : [],
              _sel: false,
              _idx: filas.length
            });
          });
        });
        self.filasVincular = filas;
        self.deteccionFiltro = '';
        self.deteccionInfo = { yaCubiertos: r.yaCubiertos || 0, totalFormatos: r.totalFormatos || 0 };
        self.deteccionCargando = false;
        self.narracion = null;
        RevisionFormatosEvents.unsubscribe(self._deteccionHandler);
      } else if (payload.estado === 'error') {
        self.deteccionCargando = false;
        self.narracion = null;
        RevisionFormatosEvents.unsubscribe(self._deteccionHandler);
        self.$f7.dialog.alert(payload.texto || 'Error en la detección.', payload.latin || 'Error');
      }
    },
    toggleNueva(idx) {
      this.nuevaAbierta = this.nuevaAbierta === idx ? null : idx;
    },
    /**
     * Clasificaciones elegibles como destino: las de la materia del formato y las de la materia
     * de la que propone la IA. Sin dedup se repetirían las que están en varias materias.
     */
    destinosDe(fila) {
      var mapa = this.clasifPorMateria || {};
      var vistos = {};
      var out = [];
      (fila.materiasDestino || []).forEach(function (mat) {
        (mapa[mat] || []).forEach(function (cl) {
          if (vistos[cl.idClasificacion]) return;
          vistos[cl.idClasificacion] = true;
          out.push(cl);
        });
      });
      return out;
    },
    /**
     * Materias que se van a añadir a los destinos marcados por vincular este formato. La materia
     * decide si la clasificación es candidata (por la del servicio del expediente) y el formato
     * acota después: declarar el formato sin su materia deja el vínculo a medias.
     */
    materiasQueSeAnaden(fila) {
      var self = this;
      if (!fila.materiaCodigo || !fila.destinos.length) return [];
      var falta = self.destinosDe(fila).some(function (cl) {
        return fila.destinos.indexOf(cl.idClasificacion) >= 0
          && (cl.materias || []).indexOf(fila.materiaCodigo) < 0;
      });
      return falta ? [fila.materiaCodigo] : [];
    },
    /** Un formato puede pertenecer a varias clasificaciones; el destino se marca y se desmarca. */
    /**
     * Saca un formato de una clasificación NUEVA propuesta y lo pasa a la lista de «encajan en
     * clasificaciones que ya existen», donde se le pueden marcar varios destinos a la vez.
     *
     * La detección asigna UNA clasificación por formato, así que un formato genérico que en
     * realidad pertenece a varias existentes no se puede expresar y sale como propuesta de
     * clasificación nueva. Crear ese cajón genérico sería un error: competiría con las
     * específicas que ya están y se llevaría expedientes que los discriminantes colocan bien.
     */
    moverAExistentes(nueva, indiceFormato) {
      var ff = (nueva.formatos || [])[indiceFormato];
      if (!ff) return;
      var yaEsta = this.filasVincular.some(function (f) { return f.codigo === ff.codigo; });
      if (!yaEsta) {
        this.filasVincular.push({
          codigo: ff.codigo,
          nombre: ff.nombre || '',
          materiaCodigo: ff.materiaCodigo || (nueva.materiasCodigos || [])[0] || null,
          // Destinos a ofrecer: la materia del formato y las de la clasificación que se proponía,
          // igual que en las filas que vienen ya resueltas del backend.
          materiasDestino: [ff.materiaCodigo].concat(nueva.materiasCodigos || []).filter(Boolean),
          expedientes: ff.expedientes || 0,
          expedientesConItem: ff.expedientesConItem || 0,
          propuesta: null,   // aquí no hay propuesta de la IA: el destino lo eliges tú
          destinos: [],
          _sel: false,
          _idx: this.filasVincular.length,
          // De dónde salió, para poder deshacerlo. La propuesta se guarda entera (no su índice)
          // porque puede desaparecer de la lista al quedarse sin formatos y hay que recomponerla.
          _origen: { nueva: nueva, formato: ff }
        });
      }
      nueva.formatos.splice(indiceFormato, 1);
      // Una clasificación nueva sin formatos ya no hay por qué crearla.
      if (!nueva.formatos.length) {
        nueva._sel = false;
        var i = this.clasifNuevas.indexOf(nueva);
        if (i >= 0) this.clasifNuevas.splice(i, 1);
      }
    },

    /** Saca todos los formatos de una propuesta nueva; la propuesta desaparece al quedarse vacía. */
    moverTodosAExistentes(nueva) {
      while ((nueva.formatos || []).length) {
        this.moverAExistentes(nueva, 0);
      }
    },

    /**
     * Para un formato que la detección da por encajado en una clasificación existente pero que
     * quien revisa cree que merece una propia: lo lleva a la pestaña «Formatos», donde la IA le
     * redacta una propuesta con tipo, subtipos y naturaleza EDITABLES y desde donde se crea.
     *
     * No se duplica aquí ese formulario a propósito: crear una clasificación necesita esos tres
     * datos, y la pantalla que ya sabe pedirlos y validarlos es aquella. Esto es solo el puente,
     * que era lo que faltaba — desde la detección no había forma de llegar.
     */
    proponerComoNueva(fila) {
      this.vistaDeteccion = false;
      this.mostrarDescartados = false;
      this.revisionFiltro = fila.codigo;
      // Dos motivos por los que podría no aparecer allí, y hay que deshacer los dos:
      //  - que esté DESCARTADO: el botón «Materia Con Clasif.» descarta en bloque y la lista se
      //    guarda en localStorage, así que sobrevive a la sesión y esconde el formato sin avisar.
      //  - que no esté CARGADO: esa pestaña va paginada de 100 en 100 y filtra en cliente, así
      //    que un formato de la página 3 no aparece por mucho que se escriba su código.
      if (this.formatosDescartados.indexOf(fila.codigo) >= 0) this.restaurar(fila.codigo);
      this._cargarHastaEncontrar(fila.codigo);
    },

    /** Pide páginas de la pestaña «Formatos» hasta que aparezca el código (o se acaben). */
    _cargarHastaEncontrar(codigo) {
      const self = this;
      if (self.revisionFormatos.some(function (f) { return f.codigo === codigo; })) {
        self.encolarProponer(codigo);
        return;
      }
      if (self.revisionFormatos.length >= self.revisionTotal) {
        self.$f7.dialog.alert('El formato «' + codigo + '» no está en la lista de formatos activos.', 'No encontrado');
        return;
      }
      self.cargarPaginaRevision(self.revisionPage + 1).then(function () {
        self._cargarHastaEncontrar(codigo);
      });
    },

    /**
     * Deshace {@link moverAExistentes}: devuelve el formato a la propuesta de clasificación nueva
     * de la que salió, recomponiéndola si se había quedado vacía y desaparecido de la lista.
     *
     * Solo aparece en las filas que se movieron a mano; las que vienen ya resueltas del backend
     * encajan de verdad en algo existente y no tienen ninguna propuesta nueva a la que volver.
     */
    devolverANueva(fila) {
      var origen = fila._origen;
      if (!origen || !origen.nueva) return;
      if (!origen.nueva.formatos) this.$set(origen.nueva, 'formatos', []);
      var yaEsta = origen.nueva.formatos.some(function (x) { return x && x.codigo === fila.codigo; });
      if (!yaEsta) origen.nueva.formatos.push(origen.formato);
      if (this.clasifNuevas.indexOf(origen.nueva) < 0) {
        origen.nueva._sel = true;
        this.clasifNuevas.push(origen.nueva);
      }
      var i = this.filasVincular.indexOf(fila);
      if (i >= 0) this.filasVincular.splice(i, 1);
    },

    toggleDestino(fila, idClasificacion) {
      var i = fila.destinos.indexOf(idClasificacion);
      if (i >= 0) fila.destinos.splice(i, 1);
      else fila.destinos.push(idClasificacion);
      // Elegir destino implica querer aplicarlo: se marca sola para no tener que dar dos clics.
      if (fila.destinos.length && !fila._sel) fila._sel = true;
    },
    /**
     * Declara los formatos marcados en su clasificación. No crea nada: solo añade el formato a
     * `formatosUsados`, que es lo que hace que la clasificación de items lo tenga en cuenta.
     */
    vincularSeleccionados() {
      const self = this;
      var seleccionadas = self.filasParaVincular;
      if (!seleccionadas.length || self.vinculando) return;
      var totalExp = seleccionadas.reduce(function (n, f) { return n + (f.expedientes || 0); }, 0);
      var totalItems = seleccionadas.reduce(function (n, f) { return n + (f.expedientesConItem || 0); }, 0);
      // Agrupado por clasificación destino: un formato puede ir a varias.
      var porClasificacion = {};
      seleccionadas.forEach(function (f) {
        f.destinos.forEach(function (id) {
          (porClasificacion[id] = porClasificacion[id] || []).push(f.codigo);
        });
      });
      var numClasificaciones = Object.keys(porClasificacion).length;
      var materias = {};
      seleccionadas.forEach(function (f) { self.materiasQueSeAnaden(f).forEach(function (m) { materias[m] = true; }); });
      var codigosMaterias = Object.keys(materias);
      var aviso = [
        'Se van a declarar ' + seleccionadas.length + ' formato(s) en ' + numClasificaciones + ' clasificación(es).',
        'Afecta a ' + totalExp + ' expediente(s), de los que ' + totalItems + ' ya tienen item creado: '
          + 'esos items solo cambian al re-sincronizarlos.'
      ]
        .concat(codigosMaterias.length
          ? ['Se añadirá también la materia <b>' + codigosMaterias.join(', ') + '</b> a las clasificaciones '
             + 'destino que no la declaren, para que esos expedientes lleguen a ellas.']
          : [])
        .concat(['Se puede deshacer quitando el formato de la clasificación (la materia no se quita sola).'])
        .join('<br><br>');
      self.$f7.dialog.confirm(
        aviso,
        'Vincular formatos',
        function () {
          self.vinculando = true;
          var asignaciones = Object.keys(porClasificacion).map(function (id) {
            return { idClasificacion: id, codigosFormato: porClasificacion[id] };
          });
          ItemClasificacion.vincularFormatos(asignaciones, true).then(function (res) {
            self.vinculando = false;
            var aplicados = (res && res.aplicados) || 0;
            // Fuera de la lista los que ya están vinculados; el resto se queda por si se quiere revisar.
            self.filasVincular = self.filasVincular.filter(function (f) {
              return seleccionadas.indexOf(f) < 0;
            });
            self.cargarMateriasPendientes();
            self.$f7.toast.create({ text: aplicados + ' formato(s) vinculado(s).', closeTimeout: 3000 }).open();
          }).catch(function (err) {
            self.vinculando = false;
            self.$f7.dialog.alert(self._msgError(err), 'Error vinculando formatos');
          });
        });
    },
    cancelarCreacion() {
      const self = this;
      if (self.creacionCancelando) return;
      self.creacionCancelando = true;
      // El cierre/aviso llega por el evento SSE 'cancelado'. Si el aviso falla, soltamos el flag.
      ItemClasificacion.cancelarCreacion().catch(function () { self.creacionCancelando = false; });
    },
    marcarTodasVisibles(valor) {
      this.clasifNuevasVisibles.forEach(function (c) { c._sel = valor; });
    },
    crearNuevasSeleccionadas() {
      const self = this;
      // Solo las visibles marcadas; las marcamos con _enCreacion para quitar SOLO esas al terminar.
      var seleccionadas = self.nuevasParaCrear;
      self.clasifNuevas.forEach(function (c) { self.$set(c, '_enCreacion', false); });
      seleccionadas.forEach(function (c) { self.$set(c, '_enCreacion', true); });
      var grupos = seleccionadas.map(function (c) {
        return {
          tipo: c.tipo,
          subtipos: c.subtipos || [],
          naturaleza: c.naturaleza || null,
          materiasCodigos: c.materiasCodigos || [],
          codigosFormato: (c.formatos || []).map(function (f) { return f.codigo; })
        };
      });
      if (!grupos.length) return;
      self.creando = true;
      self.creacionCancelando = false;
      self.narracionCreacion = { latin: 'Starting', texto: 'Lanzando la creación…', hechos: 0, total: grupos.length };
      if (!self._deteccionHandler) self._deteccionHandler = self._onDeteccionEvent.bind(self);
      // Lanzar PRIMERO (fija el estado del run actual) y suscribirse DESPUÉS (igual que la detección).
      ItemClasificacion.crearDesdeDeteccion(grupos).then(function (res) {
        RevisionFormatosEvents.subscribe(self._deteccionHandler, self.$f7);
        if (res && res.lanzado === false && res.enCurso) {
          self.$f7.toast.create({ text: 'Ya hay una creación en curso; te aviso al terminar.', closeTimeout: 3000 }).open();
        }
      }).catch(function (err) {
        self.creando = false;
        self.narracionCreacion = null;
        self.$f7.dialog.alert(self._msgError(err), 'Error lanzando la creación');
      });
    },
    // --- Detalle de características de un grupo de la propuesta (toggle, uno a la vez) ---
    toggleGrupoPropuesta(codigo, gi) {
      var key = codigo + '|' + gi;
      this.grupoAbierto = this.grupoAbierto === key ? null : key;
    },
    grupoAbiertoEs(codigo, gi) {
      return this.grupoAbierto === (codigo + '|' + gi);
    },
    grupoActivo(codigo) {
      if (!this.grupoAbierto) return null;
      var sep = this.grupoAbierto.lastIndexOf('|');
      if (sep < 0 || this.grupoAbierto.substring(0, sep) !== codigo) return null;
      var idx = parseInt(this.grupoAbierto.substring(sep + 1), 10);
      var p = this.prop(codigo);
      return p && p.propuesta && Array.isArray(p.propuesta.grupos) ? (p.propuesta.grupos[idx] || null) : null;
    },
    // --- Descartar formatos de la revisión (persistido en localStorage) ---
    cargarDescartados() {
      try {
        var raw = localStorage.getItem('itemclasif.formatosDescartados');
        this.formatosDescartados = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(this.formatosDescartados)) this.formatosDescartados = [];
      } catch (e) { this.formatosDescartados = []; }
    },
    persistirDescartados() {
      try { localStorage.setItem('itemclasif.formatosDescartados', JSON.stringify(this.formatosDescartados)); } catch (e) {}
    },
    descartar(codigo) {
      if (this.formatosDescartados.indexOf(codigo) < 0) {
        this.formatosDescartados.push(codigo);
        this.persistirDescartados();
      }
    },
    restaurar(codigo) {
      var i = this.formatosDescartados.indexOf(codigo);
      if (i >= 0) {
        this.formatosDescartados.splice(i, 1);
        this.persistirDescartados();
      }
    },
    esSinClasificar(f) {
      // "Sin clasificar" = sin propuesta y cuya materia aún no tiene clasificación.
      return !this.prop(f.codigo) && !f.yaClasificadoMateria;
    },
    descartarNoSinClasificar() {
      var self = this;
      var añadidos = self.revisionFormatos
        .filter(function (f) { return !self.esSinClasificar(f) && self.formatosDescartados.indexOf(f.codigo) < 0; })
        .map(function (f) { return f.codigo; });
      if (añadidos.length) {
        // Reasignación (no push) para forzar reactividad del filtro.
        self.formatosDescartados = self.formatosDescartados.concat(añadidos);
        self.persistirDescartados();
      }
      self.$f7.toast.create({ text: añadidos.length + ' formato(s) descartado(s) (no estaban como «sin clasificar»).', closeTimeout: 2800 }).open();
    },
    limpiarDescartados() {
      var self = this;
      self.$f7.dialog.confirm('¿Restaurar los ' + self.formatosDescartados.length + ' formatos descartados?', function () {
        self.formatosDescartados = [];
        self.persistirDescartados();
        self.mostrarDescartados = false;
      });
    },
    abrirRevision() {
      this.revisionFormatos = [];
      this.propuestasMap = {};
      this.revisionPage = 0;
      this.revisionTotal = 0;
      this.progreso = { hechos: 0, total: 0 };
      this.revisionPopupOpened = true;
      this.cargarPaginaRevision(0);
      this.cargarMateriasPendientes();
    },
    cargarMateriasPendientes() {
      const self = this;
      ItemClasificacion.materiasPendientes().then(function (lista) {
        self.materiasPendientesLista = lista || [];
        // Quita del filtro las materias que ya no tienen formatos pendientes.
        var vivas = self.materiasPendientesLista.map(function (m) { return m.codigo; });
        self.materiasFiltro = self.materiasFiltro.filter(function (c) { return vivas.indexOf(c) >= 0; });
      }).catch(function () { /* el filtro es opcional; si falla, se detecta todo */ });
    },
    toggleMateriaFiltro(codigo) {
      var i = this.materiasFiltro.indexOf(codigo);
      if (i >= 0) this.materiasFiltro.splice(i, 1);
      else this.materiasFiltro.push(codigo);
    },
    /**
     * Lanza el cálculo de las propuestas de características de las clasificaciones marcadas.
     * No guarda nada en el catálogo: deja las propuestas listas para que se revisen una a una.
     */
    lanzarSugerenciaMasiva(selecteds) {
      const self = this;
      var ids = Object.keys(selecteds || {});
      if (!ids.length) return;
      self.$f7.dialog.confirm(
        'Se calcularán las propuestas de características de ' + ids.length + ' clasificación(es).'
        + ' Tarda unos minutos y corre por detrás: cuando termine, al abrir cada una la propuesta'
        + ' aparecerá al instante. No se guarda nada en el catálogo hasta que la revises y apliques.',
        'Sugerir características',
        function () {
          ItemClasificacion.sugerenciaMasiva(ids).then(function (res) {
            if (res && res.lanzado === false && res.enCurso) {
              self.$f7.dialog.alert('Ya hay un cálculo en curso. Espera a que termine.', 'Sugerir características');
              return;
            }
            self.sugerenciaEnCurso = true;
            self.sugerenciaCancelando = false;
            self.sugerenciaAvance = { hechos: 0, total: ids.length, texto: 'Preparando…' };
            self.arrancarSondeoSugerencia();
          }).catch(function (err) {
            self.$f7.dialog.alert(self._msgError(err), 'Sugerir características');
          });
        }
      );
    },

    /**
     * Sondeo del avance. La tanda dura minutos y no puede quedarse muda si el stream no está vivo
     * o se conectó tarde; esto además cierra el aviso si terminó mientras no mirábamos.
     */
    arrancarSondeoSugerencia() {
      const self = this;
      if (self._sondeoSugerencia) return;
      self._sondeoSugerencia = setInterval(function () {
        ItemClasificacion.estadoSugerenciaMasiva().then(function (est) {
          self.sugerenciaAvance = est && est.avance ? est.avance : null;
          if (est && est.cancelando) self.sugerenciaCancelando = true;
          if (!est || !est.enCurso) {
            self.pararSondeoSugerencia();
            self.sugerenciaEnCurso = false;
            self.sugerenciaCancelando = false;
            var r = est && est.ultimoResumen;
            if (r) {
              self.$f7.dialog.alert(
                r.calculadas + ' de ' + r.total + ' propuestas calculadas'
                + (r.errores ? ', ' + r.errores + ' con error' : '')
                + '. Abre cada clasificación para revisarlas.',
                'Sugerir características');
            }
          }
        }).catch(function () { self.pararSondeoSugerencia(); self.sugerenciaEnCurso = false; });
      }, 3000);
    },

    pararSondeoSugerencia() {
      if (this._sondeoSugerencia) {
        clearInterval(this._sondeoSugerencia);
        this._sondeoSugerencia = null;
      }
    },

    /** Pide parar la tanda; lo que ya se esté calculando termina (la IA no se corta a medias). */
    cancelarSugerenciaMasiva() {
      const self = this;
      if (self.sugerenciaCancelando) return;
      self.sugerenciaCancelando = true;
      ItemClasificacion.cancelarSugerenciaMasiva().catch(function () {
        self.sugerenciaCancelando = false;
      });
    },

    /** Si al entrar ya hay una tanda corriendo (de antes o de otra pestaña), se retoma su sondeo. */
    retomarSugerenciaEnCurso() {
      const self = this;
      ItemClasificacion.estadoSugerenciaMasiva().then(function (est) {
        if (est && est.enCurso) {
          self.sugerenciaEnCurso = true;
          self.sugerenciaAvance = est.avance || null;
          self.arrancarSondeoSugerencia();
        }
      }).catch(function () {});
    },

    cargarPaginaRevision(page) {
      const self = this;
      self.revisionCargando = true;
      // Devuelve la promesa: hay quien encadena páginas hasta encontrar un formato concreto.
      return ItemClasificacion.revisionFormatos(page, self.revisionSize).then(function (res) {
        if (page === 0) self.revisionFormatos = res.list;
        else self.revisionFormatos = self.revisionFormatos.concat(res.list);
        self.revisionTotal = res.totalCount;
        self.revisionPage = page;
      }).catch(function (err) {
        self.$f7.dialog.alert(self._msgError(err), 'Error cargando formatos');
      }).finally(function () {
        self.revisionCargando = false;
      });
    },
    // --- Cola de propuestas: se puede pinchar "Proponer" en varias; se procesan de una en una ---
    enCola(codigo) {
      return this.colaProponer.indexOf(codigo) >= 0;
    },
    encolarProponer(codigo) {
      if (this.prop(codigo)) return;                       // ya tiene propuesta
      if (codigo === this.proponiendoCodigo) return;       // ya se está procesando
      if (this.colaProponer.indexOf(codigo) >= 0) return;  // ya está en cola
      this.colaProponer.push(codigo);
      this.procesarColaProponer();
    },
    quitarDeCola(codigo) {
      var i = this.colaProponer.indexOf(codigo);
      if (i >= 0) this.colaProponer.splice(i, 1);
    },
    procesarColaProponer() {
      const self = this;
      if (self.proponiendoCodigo) return;     // worker ocupado; al terminar reanuda solo
      if (!self.colaProponer.length) return;  // nada pendiente
      var codigo = self.colaProponer.shift();
      self.proponiendoCodigo = codigo;
      ItemClasificacion.proponerClasificacion(codigo).then(function (dto) {
        self.$set(self.propuestasMap, codigo, {
          codigoFormato: dto.codigoFormato || codigo,
          materiaCodigo: dto.materiaCodigo || null,
          yaClasificado: !!dto.yaClasificado,
          clasificacionExistente: dto.clasificacionExistente || null,
          propuesta: dto.propuesta || null,
          observaciones: dto.observaciones || '',
          confianza: dto.confianza,
          // Por defecto seleccionamos las propuestas nuevas (se puede desmarcar).
          _seleccionada: !dto.yaClasificado && !!dto.propuesta
        });
      }).catch(function (err) {
        self.$f7.dialog.alert(self._msgError(err), 'Error proponiendo ' + codigo);
      }).finally(function () {
        self.proponiendoCodigo = null;
        self.procesarColaProponer();  // siguiente de la cola
      });
    },
    async proponerPendientes() {
      const self = this;
      const pendientes = self.revisionFormatos.filter(f => !self.propuestasMap[f.codigo] && self.formatosDescartados.indexOf(f.codigo) < 0);
      self.progreso = { hechos: 0, total: pendientes.length };
      self.proponiendo = true;
      for (var i = 0; i < pendientes.length; i++) {
        var codigo = pendientes[i].codigo;
        self.proponiendoCodigo = codigo;
        try {
          var dto = await ItemClasificacion.proponerClasificacion(codigo);
          self.$set(self.propuestasMap, codigo, {
            codigoFormato: dto.codigoFormato || codigo,
            materiaCodigo: dto.materiaCodigo || null,
            yaClasificado: !!dto.yaClasificado,
            clasificacionExistente: dto.clasificacionExistente || null,
            propuesta: dto.propuesta || null,
            observaciones: dto.observaciones || '',
            confianza: dto.confianza,
            _seleccionada: !dto.yaClasificado && !!dto.propuesta
          });
        } catch (e) { /* seguimos con el resto */ }
        self.progreso.hechos = i + 1;
      }
      self.proponiendo = false;
      self.proponiendoCodigo = null;
    },
    setSubtipos(codigo, value) {
      var p = this.propuestasMap[codigo];
      if (!p || !p.propuesta) return;
      var arr = (value || '').split(',').map(function (s) { return s.trim(); }).filter(function (s) { return s.length; });
      this.$set(p.propuesta, 'subtipos', arr);
    },
    crearSeleccionadas() {
      const self = this;
      var payload = [];
      Object.keys(self.propuestasMap).forEach(function (codigo) {
        var p = self.propuestasMap[codigo];
        if (!p || !p._seleccionada || !p.propuesta || p.yaClasificado) return;
        var pr = p.propuesta;
        payload.push({
          codigoFormato: p.codigoFormato || codigo,
          materiaCodigo: p.materiaCodigo,
          tipo: pr.tipo,
          subtipos: pr.subtipos || [],
          naturaleza: pr.naturaleza,
          denominacion: pr.denominacion || null,
          plantillaIdItem: pr.plantillaIdItem || null,
          grupos: pr.grupos || []
        });
      });
      if (!payload.length) return;
      self.creando = true;
      // #663 · La espera de la sesión: si la sesión cambia o se sale de la pantalla en vuelo, se
      // apaga igual aunque el `then` que la apagaba no llegue a correr.
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      ItemClasificacion.crearDesdePropuestas(payload).then(function (res) {
        espera.cerrar();
        var creadas = (res && res.creadas) || 0;
        var errores = (res && res.errores) || [];
        // Marcar como ya clasificadas las creadas (las seleccionadas que no dieron error).
        payload.forEach(function (pl) {
          var codigo = pl.codigoFormato;
          var p = self.propuestasMap[codigo];
          if (p) { p.yaClasificado = true; p._seleccionada = false; }
        });
        var texto = creadas + ' clasificación(es) creada(s).' + (errores.length ? ' ' + errores.length + ' con error.' : '');
        self.$f7.toast.create({ text: texto, closeTimeout: 2500 }).open();
        if (errores.length) self.$f7.dialog.alert(errores.join('\n'), 'Errores al crear');
        if (self.$refs.rlist && typeof self.$refs.rlist.refresh === 'function') self.$refs.rlist.refresh();
      }).catch(function (err) {
        espera.cerrar();
        self.$f7.dialog.alert(self._msgError(err), 'Error creando clasificaciones');
      }).finally(function () {
        self.creando = false;
      });
    },
    _msgError(err) {
      if (!err) return 'Error desconocido';
      if (typeof err === 'string') return err;
      return err.message || (err.error && (err.error.message || err.error.code)) || JSON.stringify(err);
    },
    // ----------------------- Fusión manual de clasificaciones -----------------------
    etiquetaClasif(s) {
      var subs = (s.subtipos && s.subtipos.length) ? ' / ' + s.subtipos.join('/') : '';
      return (s.codigo || '?') + subs + (s.denominacion && s.denominacion !== s.codigo ? ' · ' + s.denominacion : '');
    },
    abrirFusion(selecteds) {
      const self = this;
      var items = Object.values(selecteds || {});
      if (items.length < 2) {
        self.$f7.dialog.alert('Selecciona al menos dos clasificaciones para fusionar.');
        return;
      }
      self.fusionSeleccionadas = items.map(function (it) {
        return { id: it.id, codigo: it.codigo, subtipos: it.subtipos || [], denominacion: it.denominacion };
      });
      self.fusionIdDestino = self.fusionSeleccionadas[0].id;
      self.fusionPreview = null;
      self.fusionResoluciones = [];
      self.fusionAplicando = false;
      self.fusionPopupOpened = true;
      self.cargarFusionPreview();
    },
    cargarFusionPreview() {
      const self = this;
      if (!self.fusionIdDestino) return;
      self.fusionCargando = true;
      self.fusionPreview = null;
      self.fusionNarracion = { latin: 'Comparing', texto: 'Comparando características de las clasificaciones…', hechos: 0, total: 0 };
      var ids = self.fusionSeleccionadas.map(function (s) { return s.id; });
      ItemClasificacion.fusionarPreview(self.fusionIdDestino, ids).then(function (preview) {
        self.fusionCargando = false;
        self.fusionNarracion = null;
        self.fusionPreview = preview || { formatosAAnadir: [], emparejadas: [], noEmparejadas: [], gruposDestino: [] };
        // Resolución por defecto de cada no emparejada: a su grupo de origen si existe en la destino; si no, ELIMINAR.
        var gruposDestino = (self.fusionPreview.gruposDestino || []).map(function (g) { return g.codigo; });
        self.fusionResoluciones = (self.fusionPreview.noEmparejadas || []).map(function (ne) {
          var enDestino = ne.origenGrupo && gruposDestino.indexOf(ne.origenGrupo) >= 0;
          return {
            accion: enDestino ? 'GRUPO_EXISTENTE' : 'ELIMINAR',
            grupoCodigo: enDestino ? ne.origenGrupo : (gruposDestino[0] || 'IDENTIFICACION'),
            grupoNuevoDenominacion: ''
          };
        });
      }).catch(function (err) {
        self.cerrarFusionPopup();
        self.$f7.dialog.alert(self._msgError(err), 'Error preparando la fusión');
      });
    },
    aplicarFusion() {
      const self = this;
      if (!self.fusionPreview || self.fusionAplicando) return;
      self.fusionAplicando = true;
      var ids = self.fusionSeleccionadas.map(function (s) { return s.id; });
      var resoluciones = (self.fusionPreview.noEmparejadas || []).map(function (ne, i) {
        var r = self.fusionResoluciones[i] || { accion: 'ELIMINAR' };
        var out = { nombre: ne.nombre, accion: r.accion };
        if (r.accion === 'ELIMINAR') return out;
        out.caracteristica = { nombre: ne.nombre, label: ne.label, descripcion: ne.descripcion, tipo: ne.tipo, aliases: ne.aliases || [] };
        if (r.accion === 'GRUPO_EXISTENTE') {
          out.grupoCodigo = r.grupoCodigo;
        } else if (r.accion === 'GRUPO_NUEVO') {
          var deno = (r.grupoNuevoDenominacion || '').trim() || 'NUEVO';
          out.grupoNuevo = { codigo: self._codigoGrupo(deno), denominacion: deno, orden: null, obligatorio: false };
        }
        return out;
      });
      var payload = { idDestino: self.fusionIdDestino, idsFusionar: ids, emparejadas: self.fusionPreview.emparejadas || [], resoluciones: resoluciones };
      ItemClasificacion.fusionarAplicar(payload).then(function (res) {
        self.cerrarFusionPopup();
        var msg = 'Fusión completada: +' + (res.formatosAnadidos || 0) + ' formatos, +' + (res.aliasAplicados || 0) + ' alias, +' + (res.caracteristicasCreadas || 0) + ' características. ' + (res.clasifFusionadas || 0) + ' clasificación(es) marcadas como fusionadas.';
        self.$f7.toast.create({ text: msg, closeTimeout: 4000, closeButton: true }).open();
        if (self.$refs.rlist && self.$refs.rlist.reload) self.$refs.rlist.reload();
      }).catch(function (err) {
        self.fusionAplicando = false;
        self.$f7.dialog.alert(self._msgError(err), 'Error aplicando la fusión');
      });
    },
    _codigoGrupo(denominacion) {
      return (denominacion || '').toString().trim().toUpperCase()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'NUEVO';
    },
    cerrarFusionPopup() {
      this.fusionPopupOpened = false;
      this.fusionCargando = false;
      this.fusionAplicando = false;
      this.fusionNarracion = null;
      this.fusionPreview = null;
      // F7 deja el backdrop huérfano y la clase with-modal-popup (que bloquea el scroll de la
      // página) al desmontar con v-if un popup abierto; lo limpiamos si no queda ningún popup.
      setTimeout(function () {
        document.querySelectorAll('.popup-backdrop.backdrop-in').forEach(function (el) { el.classList.remove('backdrop-in'); });
        if (!document.querySelector('.popup.modal-in')) {
          document.documentElement.classList.remove('with-modal-popup', 'with-modal-popup-push', 'with-modal-popup-push-closing');
        }
      }, 50);
    },

    // ---------------- Generar items enriquecidos ----------------
    abrirGenerar() {
      const self = this;
      self.generarPopupOpened = true;
      self.generarPreviewTotal = null;
      if (!self.generarCampos.length) {
        GeneracionItems.campos().then(function (campos) {
          // Ordenados alfabéticamente por etiqueta para localizarlos rápido en el combo.
          var lista = (campos || []).slice().sort(function (a, b) {
            return (a.label || a.clave || '').localeCompare(b.label || b.clave || '', 'es', { sensitivity: 'base' });
          });
          // Texto mostrado en el autocomplete; si hay etiquetas repetidas, se desambigua con la clave.
          var porLabel = {};
          lista.forEach(function (c) { var l = c.label || c.clave; porLabel[l] = (porLabel[l] || 0) + 1; });
          lista.forEach(function (c) {
            var l = c.label || c.clave;
            c._display = porLabel[l] > 1 ? l + ' (' + c.clave + ')' : l;
          });
          self.generarCampos = lista;
          var map = {};
          self.generarCampos.forEach(function (c) { map[c.clave] = c; });
          self.generarCamposMap = map;
          self._aplicarFiltroPorDefecto();
        }).catch(function (err) {
          self.$f7.dialog.alert(self._msgError(err), 'Error cargando campos');
        });
      } else {
        self._aplicarFiltroPorDefecto();
      }
    },
    /**
     * Filtro base si no hay condiciones (al entrar al popup o al quedarse vacío): fecha de
     * inspección del último mes + estado del expediente ENVIADO. Si ya hay condiciones, se respetan.
     */
    _aplicarFiltroPorDefecto() {
      if (!this.generarCondiciones.length) {
        var hoy = new Date();
        var haceUnMes = new Date(hoy.getFullYear(), hoy.getMonth() - 1, hoy.getDate());
        var condiciones = [];
        if (this.generarCamposMap['fechaInsp']) {
          condiciones.push({ campo: 'fechaInsp', operador: 'BETWEEN', valores: [this._fmtFecha(haceUnMes), this._fmtFecha(hoy)] });
        }
        if (this.generarCamposMap['estado.estado']) {
          condiciones.push({ campo: 'estado.estado', operador: 'IN', valores: ['ENVIADO'] });
        }
        this.generarCombinador = 'AND';
        this.generarCondiciones = condiciones.length ? condiciones : [{ campo: '', operador: '', valores: [] }];
      }
      this.schedulePreview();
    },
    _fmtFecha(d) {
      var mm = String(d.getMonth() + 1).padStart(2, '0');
      var dd = String(d.getDate()).padStart(2, '0');
      return d.getFullYear() + '-' + mm + '-' + dd;
    },
    cerrarGenerarPopup() {
      this.generarPopupOpened = false;
      this.generarLanzando = false;
      this.generarNarracion = null;
      if (this._generarHandler) GeneracionItemsEvents.unsubscribe(this._generarHandler);
      setTimeout(function () {
        document.querySelectorAll('.popup-backdrop.backdrop-in').forEach(function (el) { el.classList.remove('backdrop-in'); });
        if (!document.querySelector('.popup.modal-in')) {
          document.documentElement.classList.remove('with-modal-popup', 'with-modal-popup-push', 'with-modal-popup-push-closing');
        }
      }, 50);
    },
    campoDe(cond) { return this.generarCamposMap[cond.campo] || null; },
    /** Texto que muestra el autocomplete de campo para una condición. */
    labelDe(cond) {
      var c = this.campoDe(cond);
      return c ? (c._display || c.label || c.clave) : '';
    },
    /** Resuelve el texto tecleado/elegido en el autocomplete a un campo del catálogo. */
    onCampoInput(cond, texto) {
      var t = (texto || '').trim().toLowerCase();
      var encontrado = null;
      if (t) {
        encontrado = this.generarCampos.find(function (c) {
          return (c._display || '').toLowerCase() === t
            || (c.label || '').toLowerCase() === t
            || (c.clave || '').toLowerCase() === t;
        }) || null;
      }
      if (encontrado) {
        if (cond.campo !== encontrado.clave) {
          cond.campo = encontrado.clave;
          this.onCampoChange(cond);
        }
      } else {
        // Texto que no casa con ningún campo: se limpia la condición (y el input al re-render).
        cond.campo = '';
        cond.operador = '';
        this.$set(cond, 'valores', []);
        this.schedulePreview();
      }
    },
    tipoDe(cond) { var c = this.campoDe(cond); return c ? c.tipo : null; },
    operadoresDe(cond) { var c = this.campoDe(cond); return c ? (c.operadores || []) : []; },
    opcionesDe(cond) { var c = this.campoDe(cond); return c ? (c.opciones || []) : []; },
    necesitaValor(cond) {
      return ['IS_TRUE', 'IS_FALSE', 'VACIO', 'RELLENO'].indexOf(cond.operador) < 0;
    },
    inputType(cond) {
      var t = this.tipoDe(cond);
      if (t === 'DATE') return 'date';
      if (t === 'NUMBER') return 'number';
      return 'text';
    },
    etiquetaOperador(op) {
      var m = {
        EQUALS: '=', EQUALS_IGNORE_CASE: '= (sin mayúsc.)', NOT_EQUALS: '≠',
        IN: 'en', IN_IGNORE_CASE: 'en (sin mayúsc.)', NOT_IN: 'no en',
        LIKE: 'contiene (no distingue mayúsc.)', NOT_CONTAINS_IGNORE_CASE: 'no contiene (sin mayúsc.)',
        GTE: '≥', LTE: '≤', BETWEEN: 'entre', IS_TRUE: 'sí', IS_FALSE: 'no',
        VACIO: 'vacío', RELLENO: 'relleno', EXISTS: 'existe'
      };
      return m[op] || op;
    },
    onCampoChange(cond) {
      var ops = this.operadoresDe(cond);
      cond.operador = ops.length ? ops[0] : '';
      this.$set(cond, 'valores', []);
      this.schedulePreview();
    },
    onOperadorChange(cond) {
      // Al cambiar de operador puede cambiar el nº de valores; reseteamos.
      // EXISTS (catálogos antiguos) arranca con SÍ para que el combo y el payload coincidan.
      this.$set(cond, 'valores', cond.operador === 'EXISTS' ? ['true'] : []);
      this.schedulePreview();
    },
    setVal(cond, idx, val) {
      if (!Array.isArray(cond.valores)) this.$set(cond, 'valores', []);
      this.$set(cond.valores, idx, val);
      this.schedulePreview();
    },
    addCondicion() {
      this.generarCondiciones.push({ campo: '', operador: '', valores: [] });
    },
    removeCondicion(i) {
      this.generarCondiciones.splice(i, 1);
      if (!this.generarCondiciones.length) {
        // Nunca sin filtros: al vaciarse, vuelven los dos por defecto (evita contar/generar
        // sobre TODOS los expedientes por accidente).
        this._aplicarFiltroPorDefecto();
        return;
      }
      this.schedulePreview();
    },
    _filtroPayload() {
      var self = this;
      var condiciones = this.generarCondiciones.map(function (c) {
        var campo = self.generarCamposMap[c.campo];
        if (!campo || !c.operador) return null;
        var tipo = campo.tipo;
        var valores = [];
        if (['IS_TRUE', 'IS_FALSE', 'VACIO', 'RELLENO'].indexOf(c.operador) >= 0) {
          valores = [];
        } else if (c.operador === 'BETWEEN') {
          valores = [c.valores[0], c.valores[1]].filter(function (v) { return v !== undefined && v !== null && v !== ''; });
          if (valores.length < 2) return null;
        } else if (c.operador === 'IN' || c.operador === 'NOT_IN' || c.operador === 'IN_IGNORE_CASE') {
          if (tipo === 'ENUM') {
            valores = (c.valores || []).slice();
          } else {
            valores = String(c.valores[0] || '').split(/[\n,]+/).map(function (s) { return s.trim(); }).filter(Boolean);
          }
          if (!valores.length) return null;
        } else {
          var v0 = c.valores[0];
          if (v0 === undefined || v0 === null || v0 === '') return null;
          valores = [String(v0)];
        }
        return { campo: c.campo, operador: c.operador, valores: valores };
      }).filter(function (c) { return c && c.campo && c.operador; });
      return {
        combinador: this.generarCombinador,
        condiciones: condiciones,
        soloSinItem: this.generarSoloSinItem
      };
    },
    schedulePreview() {
      var self = this;
      if (this._generarPreviewTimer) clearTimeout(this._generarPreviewTimer);
      this._generarPreviewTimer = setTimeout(function () { self.previsualizarGenerar(); }, 400);
    },
    previsualizarGenerar() {
      var self = this;
      var payload = self._filtroPayload();
      if (!payload.condiciones.length) {
        // Sin condiciones efectivas (vacías o incompletas) no se cuenta ni se permite generar
        // sobre TODOS los expedientes.
        self.generarPreviewCargando = false;
        self.generarPreviewTotal = null;
        return;
      }
      self.generarPreviewCargando = true;
      GeneracionItems.preview(payload).then(function (res) {
        self.generarPreviewCargando = false;
        self.generarPreviewTotal = res && typeof res.total === 'number' ? res.total : 0;
      }).catch(function () {
        self.generarPreviewCargando = false;
        self.generarPreviewTotal = null;
      });
    },
    lanzarGeneracion() {
      var self = this;
      if (self.generarLanzando) return;
      self.generarLanzando = true;
      self.generarNarracion = { latin: 'Starting', texto: 'Lanzando generación…', hechos: 0, total: 0 };
      if (!self._generarHandler) self._generarHandler = self._onGenerarEvent.bind(self);
      GeneracionItems.lanzar(self._filtroPayload()).then(function (res) {
        GeneracionItemsEvents.subscribe(self._generarHandler, self.$f7);
        if (res && res.lanzado === false && res.enCurso) {
          self.$f7.toast.create({ text: 'Ya hay una generación en curso…', closeTimeout: 3000 }).open();
        }
      }).catch(function (err) {
        self.generarLanzando = false;
        self.$f7.dialog.alert(self._msgError(err), 'Error');
      });
    },
    cancelarGeneracion() {
      GeneracionItems.cancelar().catch(function () {});
    },
    _onGenerarEvent(payload) {
      var self = this;
      if (!payload) return;
      if (payload.estado === 'progreso') {
        self.generarLanzando = true;
        self.generarNarracion = { latin: payload.latin, texto: payload.texto, hechos: payload.hechos, total: payload.total };
      } else if (payload.estado === 'fin' || payload.estado === 'cancelado' || payload.estado === 'error') {
        self.generarLanzando = false;
        self.generarNarracion = null;
        GeneracionItemsEvents.unsubscribe(self._generarHandler);
        var resumen = payload.resumen || {};
        if (payload.estado === 'error') {
          self.$f7.dialog.alert(payload.texto || 'Error', payload.latin || 'Error');
        } else if (resumen.fallidos > 0 && resumen.errores && resumen.errores.length) {
          // Hubo fallos: mostramos el detalle por expediente para saber POR QUÉ no se generaron.
          var detalle = resumen.errores.map(function (e) { return '• ' + e; }).join('<br>');
          self.$f7.dialog.alert(
            (payload.texto || '') + '<br><br><div style="text-align:left; font-size:12px; max-height:40vh; overflow:auto;">' + detalle + '</div>',
            'Generación con fallos'
          );
        } else {
          self.$f7.toast.create({ text: (payload.estado === 'fin' ? '✅ ' : '⏹️ ') + (payload.texto || ''), closeTimeout: 4000, closeButton: true }).open();
        }
      }
    },
    onImportClick() {
      const self = this;
      const triggerUpsert = function () {
        self._pendingMode = 'UPSERT';
        self.$refs.fileInput.click();
      };
      const triggerReplace = function () {
        self.$f7.dialog.confirm('Esto BORRARÁ todas las clasificaciones actuales. ¿Continuar?', () => {
          self._pendingMode = 'REPLACE_ALL';
          self.$refs.fileInput.click();
        });
      };
      const dialog = self.$f7.dialog.create({
        title: 'Importar clasificaciones desde Excel',
        content: ''
          + '<div style="padding:8px 16px; font-size:13px; line-height:1.7;">'
          + '  <p style="margin:0 0 10px 0;">'
          + '    <button class="button button-fill button-small" data-action="upsert" style="display:inline-flex; vertical-align:middle; margin-right:8px; padding:0 10px;">ACTUALIZAR</button>'
          + '    crea/actualiza por código+subtipos sin tocar el resto.'
          + '  </p>'
          + '  <p style="margin:0;">'
          + '    <button class="button button-fill button-small color-red" data-action="replace" style="display:inline-flex; vertical-align:middle; margin-right:8px; padding:0 10px;">REEMPLAZAR TODO</button>'
          + '    borra TODAS las clasificaciones antes de importar.'
          + '  </p>'
          + '</div>',
        buttons: [{ text: 'Cancelar', close: true }]
      });
      dialog.on('open', function () {
        const $$ = self.$$;
        const $el = $$(dialog.el);
        $el.find('[data-action="upsert"]').on('click', function () {
          dialog.close();
          triggerUpsert();
        });
        $el.find('[data-action="replace"]').on('click', function () {
          dialog.close();
          triggerReplace();
        });
      });
      dialog.open();
    },
    onFileSelected(e) {
      const self = this;
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      const mode = self._pendingMode || 'UPSERT';
      const espera = preloaderDeLaSesion(self);
      espera.mostrar();
      ItemClasificacion.importExcel(file, mode).then((res) => {
        espera.cerrar();
        self._showResultadoImport(res);
        if (self.$refs.rlist && self.$refs.rlist.refresh) self.$refs.rlist.refresh();
        self.aplicarTiposDelMaestro();
      }).catch((err) => {
        espera.cerrar();
        self.$f7.dialog.alert(typeof err === 'string' ? err : (err.message || JSON.stringify(err)),
          'Importar Excel');
      });
    },
    _showResultadoImport(res) {
      const self = this;
      const creadas = res.creadas || 0;
      const actualizadas = res.actualizadas || 0;
      const errores = res.errores || [];
      let html = '<p><b>Creadas:</b> ' + creadas
        + ' &nbsp; <b>Actualizadas:</b> ' + actualizadas + '</p>';
      if (errores.length) {
        html += '<p><b>Errores (' + errores.length + '):</b></p>';
        html += '<ul style="max-height:300px;overflow:auto;padding-left:18px">';
        errores.forEach((er) => {
          html += '<li><b>' + (er.sheet || '') + '</b>'
            + (er.fila ? ' (fila ' + er.fila + ')' : '') + ': ' + (er.mensaje || '') + '</li>';
        });
        html += '</ul>';
      }
      self.$f7.dialog.create({
        title: 'Resultado de la importación',
        text: html,
        buttons: [{ text: 'OK', close: true }]
      }).open();
    },
    urlVerTipo(codigo) {
      // URL absoluta para abrir la ficha del tipo. Solo origin + hash-bang route.
      return window.location.origin + '/#!/itemtipoclasificacion/view/' + encodeURIComponent(codigo) + '/';
    },
    abrirVerTipo(codigo) {
      // Abrimos via window.open para evitar que el router de Framework7 capture el
      // clic en el <a> (target=_blank no basta porque F7 intercepta hrefs internos).
      window.open(this.urlVerTipo(codigo), '_blank', 'noopener');
    },
    aplicarTiposDelMaestro() {
      const self = this;
      Promise.all([
        ItemTipoClasificacion.getList({ page: 1, limit: 1000 }, true),
        self.esperarColumnasCargadas()
      ]).then(function (results) {
        var res = results[0];
        var items = (res && (res.list || res.content || res.data || res)) || [];
        if (!Array.isArray(items) && items.content) items = items.content;
        var tipos = (items || []).filter(function (t) { return t && t.codigo; });
        if (!tipos.length) return;
        tipos.sort(function (a, b) { return a.codigo.localeCompare(b.codigo); });

        var rlist = self.$refs.rlist;
        if (!rlist || !rlist.columns) return;
        var col = rlist.columns.find(function (c) { return c.pathName === 'codigo'; });
        if (!col) return;

        if (col.filtro) {
          col.filtro.items = tipos.map(function (t) {
            return { nombre: t.codigo + (t.denominacion ? ' - ' + t.denominacion : ''), valor: t.codigo };
          });
        }
        col.colores = tipos.map(function (t) {
          return { nombre: t.codigo, valor: t.colorBadge || 'gray' };
        }).concat([{ nombre: 'DEFAULT', valor: 'gray' }]);

        // Mapa codigo → color que usa el slot v-slot:column-codigo para pintar el badge.
        var mapaColores = {};
        tipos.forEach(function (t) { mapaColores[t.codigo] = t.colorBadge || 'gray'; });
        self.coloresPorCodigo = mapaColores;
        // Los mismos tipos alimentan el selector del popup de discriminantes.
        self.tiposMaestro = tipos;

        if (rlist.$forceUpdate) rlist.$forceUpdate();
        self.$forceUpdate();
      }).catch(function () { /* silencio: el listado funciona con el seed estático */ });
    },
    aplicarNaturalezasDelMaestro() {
      const self = this;
      Promise.all([
        NaturalezasMaestro.load(),
        self.esperarColumnasCargadas()
      ]).then(function (results) {
        var m = results[0];
        if (!m || !m.items || !m.items.length) return;
        var rlist = self.$refs.rlist;
        if (!rlist || !rlist.columns) return;
        var col = rlist.columns.find(function (c) { return c.pathName === 'naturaleza'; });
        if (!col) return;

        col.colores = m.items.map(function (n) {
          return { nombre: n.valor, valor: n.badgeColor || 'gray' };
        }).concat([{ nombre: 'DEFAULT', valor: 'gray' }]);

        if (col.filtro) {
          col.filtro.items = m.items.map(function (n) {
            return { nombre: n.label, valor: n.valor };
          });
        }
        if (col.cambioMasivo) {
          col.cambioMasivo.items = m.items.map(function (n) {
            return { nombre: n.label, valor: n.valor };
          });
        }
        if (rlist.$forceUpdate) rlist.$forceUpdate();
        self.$forceUpdate();
      }).catch(function () { /* silencio: si falla, queda lo del JSON */ });
    },
    esperarColumnasCargadas() {
      const self = this;
      return new Promise(function (resolve) {
        var tries = 0;
        var check = function () {
          var rlist = self.$refs.rlist;
          if (rlist && Array.isArray(rlist.columns) && rlist.columns.length > 0) return resolve();
          if (++tries > 100) return resolve();
          setTimeout(check, 100);
        };
        check();
      });
    }
  }
};
</script>

<style scoped>
/* ---- Generar items: ayuda del filtro dentro del modal ---- */
.generar-ayuda {
  margin: 0 0 10px 0;
  padding: 8px 12px;
  background: #eef6ee;
  border: 1px solid #d5e8d5;
  border-left: 4px solid #7cb87c;
  border-radius: 8px;
  font-size: 12.5px;
}
.generar-ayuda ul {
  margin: 4px 0;
  padding-left: 18px;
}
.generar-ayuda li {
  margin-bottom: 4px;
}
/* ---- Generar items: cada condición del filtro en su propia línea, sombreada ---- */
.cond-row {
  position: relative; /* ancla del botón de eliminar */
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: nowrap; /* una línea: campo | operador | valores */
  width: 100%;
  margin-bottom: 4px;
  padding: 3px 34px 3px 10px; /* vertical mínimo; hueco a la derecha para el botón flotante */
  background: #f2f4f7;
  border: 1px solid #e2e6ec;
  border-left: 4px solid #b7c3d4;
  border-radius: 8px;
}
/* Columnas: campo y operador con ancho fijo; los valores se reparten el resto. */
.cond-row .cond-campo {
  flex: 0 0 220px;
  min-width: 0;
}
.cond-row .cond-op {
  flex: 0 0 110px;
  min-width: 0;
}
.cond-row .cond-val {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  gap: 6px;
  align-items: center;
}
.cond-row .cond-val > input,
.cond-row .cond-val > select {
  flex: 1 1 0;
  width: 100%;
  min-width: 0;
}
/* Multiselect (enum) compacto para no disparar la altura de la fila. */
.cond-row .cond-val > select[multiple] {
  min-height: 72px;
  max-height: 96px;
}
.cond-row:nth-child(even) {
  background: #eaeef4;
}
/* Botón de eliminar flotando en la esquina superior derecha del contenedor. */
.cond-row > .link:last-child {
  position: absolute;
  top: 6px;
  right: 6px;
}
/* Campos de cada condición: borde blanco y fondo azul claro para que resalten sobre la fila. */
.cond-row input,
.cond-row select {
  background: #e3f0fb;
  border: 2px solid #fff;
  border-radius: 6px;
  padding: 4px 8px;
  outline: none;
}
.cond-row input:focus,
.cond-row select:focus {
  border-color: #9ec9ef;
}
.tablelinks-menu {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}
.tablelink-row {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  line-height: 1.4;
}
.tablelink-row i.f7-icons {
  font-size: 17px;
  width: 18px;
  text-align: center;
  flex: 0 0 18px;
}
.revision-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border-bottom: 1px solid #e0e4ea;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 10;
}
.revision-modos {
  display: flex;
  gap: 8px;
  padding: 8px 14px 0;
}
/* Título del modal con fondo amarillo (el texto IA azul no se ve sobre la navbar oscura). */
.titulo-revision {
  background: #ffd54f;
  padding: 2px 12px;
  border-radius: 6px;
  font-weight: 700;
}
.deteccion-pensando {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 28px;
}
.creacion-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 14px;
  background: #fff8e1;
  border-bottom: 1px solid #ffe082;
  font-size: 13px;
}
.deteccion-narracion {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.deteccion-latin {
  font-size: 15px;
  font-style: italic;
}
.deteccion-texto {
  font-size: 13px;
  color: #333;
}
.deteccion-progreso {
  font-size: 11px;
}
.revision-lista {
  padding: 10px 12px;
}
.revision-card {
  position: relative;
  border: 1px solid #d0d4dc;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
  background: #fafbfd;
}
.revision-card-nueva {
  border-color: #b39ddb;
  background: #f6f2fc;
}
/* Azul (no morado) para distinguir de un vistazo lo que se VINCULA a algo que ya existe
   de lo que se CREA nuevo: son dos acciones distintas en la misma pantalla. */
.revision-card-existente {
  border-color: #90caf9;
  background: #f1f7fe;
}
.revision-subtitulo {
  font-size: 12px;
  font-weight: 600;
  margin: 4px 0 6px;
  color: #1565c0;
}
.revision-subtitulo .text-color-gray {
  font-weight: 400;
  font-size: 11px;
}
.rp-impacto {
  font-size: 9px;
  color: #1565c0;
  margin-left: 5px;
  white-space: nowrap;
}
.destino-label {
  font-size: 11px;
  color: #666;
  margin-right: 4px;
}
.destino-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  border: 1px solid #cfd8e3;
  border-radius: 11px;
  padding: 1px 9px;
  margin: 2px 4px 2px 0;
  background: #fff;
  color: #555;
  cursor: pointer;
  user-select: none;
}
.destino-chip:hover { border-color: #90caf9; }
.destino-chip-on {
  background: #1565c0;
  border-color: #1565c0;
  color: #fff;
  font-weight: 600;
}
.destino-ia { font-size: 11px; opacity: .75; }
.destino-aviso {
  font-size: 11px;
  color: #8a6d1f;
  background: #fff8e1;
  border: 1px solid #ffe0a3;
  border-radius: 5px;
  padding: 3px 8px;
  margin-top: 4px;
}
.revision-head {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.revision-codigo {
  font-family: monospace;
  font-weight: 700;
  font-size: 12px;
}
.estado-badge {
  font-size: 10px;
  padding: 2px 8px;
}
.revision-btn-proponer {
  margin-left: auto;
}
.revision-filtro {
  flex: 1 1 200px;
  max-width: 340px;
  padding: 5px 10px;
  border: 1px solid #b6cee6;
  border-radius: 6px;
  font-size: 13px;
}
.revision-btn-descartar {
  margin-left: auto;
  font-size: 12px;
  cursor: pointer;
}
.revision-btn-pendiente {
  font-size: 12px;
  font-weight: 600;
  color: #f57c00;
  cursor: pointer;
}
.revision-btn-pendiente:hover {
  color: #e65100;
  text-decoration: line-through;
}
.revision-card-descartada {
  opacity: 0.65;
  border-style: dashed;
}
/* Cerebro "pensando" mientras la IA redacta la propuesta */
.brain-thinking {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.brain-svg {
  width: 30px;
  height: 30px;
  overflow: visible;
}
.brain-path {
  fill: rgba(0, 0, 255, 0.08);
  stroke: #0000ff;
  stroke-width: 16;
  stroke-linejoin: round;
  animation: brainGlow 1.6s ease-in-out infinite;
}
@keyframes brainGlow {
  0%, 100% { stroke: #0000ff; filter: drop-shadow(0 0 1px rgba(0, 0, 255, 0.5)); }
  50%      { stroke: #3a6dff; filter: drop-shadow(0 0 5px rgba(0, 90, 255, 0.85)); }
}
.spark {
  fill: #ffffff;
  opacity: 0.12;
  transform-box: fill-box;
  transform-origin: center;
  animation: sparkFire 1.2s ease-in-out infinite;
}
.brain-sparks .spark:nth-child(1) { animation-delay: 0s; }
.brain-sparks .spark:nth-child(2) { animation-delay: 0.3s; }
.brain-sparks .spark:nth-child(3) { animation-delay: 0.6s; }
.brain-sparks .spark:nth-child(4) { animation-delay: 0.9s; }
.brain-sparks .spark:nth-child(5) { animation-delay: 1.2s; }
@keyframes sparkFire {
  0%, 100% { opacity: 0.12; filter: none; }
  45%      { opacity: 1; filter: drop-shadow(0 0 3px #7fb3ff); }
}
.spark-travel-a { animation: sparkFire 1.2s ease-in-out infinite, travelA 2.6s ease-in-out infinite; }
.spark-travel-b { animation: sparkFire 1.2s ease-in-out infinite, travelB 3.1s ease-in-out infinite; }
@keyframes travelA { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(110px, 80px); } }
@keyframes travelB { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(130px, -60px); } }
.brain-text {
  font-size: 11px;
}
/* Indicador "pensando" centrado sobre la propia card */
.card-brain-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(246, 242, 252, 0.85);
  border-radius: 6px;
  z-index: 5;
}
.brain-svg-card {
  width: 46px;
  height: 46px;
}
.brain-text-card {
  font-size: 14px;
  letter-spacing: 1px;
}
.revision-check input {
  width: 18px;
  height: 18px;
}
.revision-propuesta {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #d4c9ec;
}
.rp-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}
.rp-row label {
  font-size: 11px;
  font-weight: 600;
  color: #455a64;
}
.rp-input {
  padding: 4px 8px;
  border: 1px solid #c5b6e0;
  border-radius: 5px;
  font-size: 13px;
  background: #fff;
}
.rp-input-wide { min-width: 220px; }
.rp-input-full { flex: 1 1 100%; font-family: monospace; }
.rp-grupos {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.rp-grupo {
  font-size: 10px;
  background: #ede7f6;
  color: #4527a0;
  border-radius: 8px;
  padding: 2px 8px;
  cursor: pointer;
  user-select: none;
}
.rp-grupo:hover {
  background: #d7ccef;
}
.rp-grupo-activo {
  background: #4527a0;
  color: #fff;
}
.rp-grupo-detalle {
  margin-top: 6px;
  padding: 6px 8px;
  background: #f6f3fc;
  border: 1px solid #e0d7f3;
  border-radius: 6px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
}
.rp-grupo-detalle-titulo {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #4527a0;
  margin-right: 4px;
}
.rp-caract {
  font-size: 11px;
  background: #fff;
  border: 1px solid #d7ccef;
  border-radius: 6px;
  padding: 1px 7px;
  color: #333;
}
.rp-caract-tipo {
  font-size: 9px;
  color: #8a7bb5;
  margin-left: 4px;
}
.rp-mover {
  font-size: 9px;
  margin-left: 6px;
  font-weight: 600;
  display: inline;
}
.rp-obs {
  margin-top: 6px;
  font-size: 11px;
  color: #6a4caf;
  font-style: italic;
}
.revision-yaclasif {
  margin-top: 6px;
  font-size: 12px;
  color: #00695c;
}
.revision-materias-filtro {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 6px 14px;
  border-bottom: 1px solid #eef1f5;
}
.materia-chip {
  display: inline-block;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid #b6cee6;
  border-radius: 12px;
  background: #fff;
  color: #2b6cb0;
  cursor: pointer;
  user-select: none;
}
.materia-chip-on {
  background: #2b6cb0;
  border-color: #2b6cb0;
  color: #fff;
}
/* ---- Fusión manual de clasificaciones ---- */
.fusion-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border-bottom: 1px solid #e0e4ea;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 10;
}
.fusion-select, .fusion-input {
  border: 1px solid #b6cee6;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 12px;
  max-width: 100%;
}
.fusion-cuerpo { padding: 8px 14px 24px; }
.fusion-bloque { margin-bottom: 14px; }
.fusion-bloque-tit {
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
  color: #4a148c;
}
.fusion-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.fusion-chip { padding: 2px 8px; font-size: 11px; }
.fusion-empareja {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 3px 0;
  font-size: 12px;
}
.fusion-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.fusion-tabla th, .fusion-tabla td {
  text-align: left;
  padding: 4px 6px;
  border-bottom: 1px solid #eef1f5;
  vertical-align: top;
}
.fusion-tabla th { color: #888; font-weight: 600; }

/* --- Discriminantes de subtipo --- */
.disc-aviso {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #8a6d3b;
  background: #fcf8e3;
  border: 1px solid #faebcc;
  border-radius: 4px;
  padding: 4px 8px;
  margin-bottom: 4px;
}
.disc-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 10px 12px;
  padding: 8px 10px;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  background: #fff;
}
.disc-card-head {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.disc-cond {
  padding: 6px 0;
  border-top: 1px dashed #eef1f5;
}
.disc-cond-linea {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.disc-si { font-size: 12px; color: #666; }
.disc-carac-wrap { display: inline-flex; flex-direction: column; gap: 1px; }
.disc-carac { min-width: 150px; max-width: 230px; }
.disc-nombre-tec { font-size: 10px; color: #8a8f98; padding-left: 2px; }
.disc-opc-count { color: #b36b00; margin-left: 5px; }
.disc-op { min-width: 130px; }
/* Los valores que cumplen: siempre visibles, cada uno como chip que se puede quitar. */
.disc-valores {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  margin: 5px 0 0 20px;
}
.disc-valores-lbl { font-size: 11px; color: #888; }
.chip-valor {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #e8f0fe;
  color: #1a4b8c;
  border: 1px solid #c5d9f7;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 11px;
  cursor: pointer;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chip-valor .chip-x { font-size: 13px; color: #7089b0; flex: none; }
.chip-valor:hover { background: #d8e6fd; }
.disc-falta { font-size: 11px; color: #c0392b; font-weight: 600; }
.disc-add { min-width: 160px; font-size: 11px; }
.disc-add-libre { width: 180px; }
.disc-leyenda {
  margin: 8px 12px;
  padding: 7px 10px;
  background: #f4f7fb;
  border: 1px solid #e1e8f2;
  border-radius: 6px;
  font-size: 11px;
  line-height: 1.5;
  color: #555;
}
.disc-just {
  font-size: 10px;
  font-style: italic;
  color: #999;
  margin: 3px 0 0 20px;
}
.disc-empate {
  border: 1px solid #f3d6d6;
  background: #fff8f8;
  border-radius: 4px;
  padding: 4px 8px;
  margin: 4px 0;
}
</style>
